import { useState, useReducer, useMemo, useCallback, useRef } from "react";

/* ═══════════════════════════════════════════════════════════════════════
   e2eClm.jsx — CLM End-to-End Implementation Gap Coverage (PRODUCTION v2.0)
   Enhanced with: Comprehensive PDF Export, Detailed Views, State Management
   Covers everything NOT in App.jsx but REQUIRED for a complete
   Siemens Closed-Loop Manufacturing deployment.
   ═══════════════════════════════════════════════════════════════════════ */

// ─── COLOUR PALETTE ──────────────────────────────────────────────────────
const C = {
  bg: "#020c18", bg2: "#041020", bg3: "#071628",
  cyan: "#00e5ff", green: "#00ff88", gold: "#ffd600",
  amber: "#ffb300", blue: "#38a8ff", teal: "#00d4c0",
  purple: "#c084fc", pink: "#ff4f8b", red: "#ff4444",
  orange: "#fb923c", steel: "#64b5d6",
  text: "#ddeeff", dim: "#6a8fa8", dark: "#2e4a60",
};

// ─── STYLES ──────────────────────────────────────────────────────────────
const S = {
  kpiCard: {
    padding: "10px 16px", background: "rgba(255,255,255,.02)",
    border: "1px solid rgba(255,255,255,.06)", borderRadius: 6,
    textAlign: "center", minWidth: 90, flex: "0 1 auto"
  },
  pill: (color, sm) => ({
    display: "inline-block", padding: sm ? "1px 5px" : "2px 8px",
    borderRadius: 12, fontFamily: "'Share Tech Mono', monospace",
    fontSize: sm ? 7 : 8.5, border: `1px solid ${color}40`,
    background: `${color}12`, color
  }),
  progressBar: {
    height: 4, background: "rgba(255,255,255,.06)", borderRadius: 2, overflow: "hidden"
  },
  tab: (active, color) => ({
    fontFamily: "'Orbitron', monospace", fontSize: 9, fontWeight: 600,
    padding: "9px 16px", cursor: "pointer",
    color: active ? (color || C.red) : C.dim,
    borderBottom: `2px solid ${active ? (color || C.red) : "transparent"}`,
    background: active ? "rgba(255,68,68,.04)" : "transparent",
    transition: "all .15s", whiteSpace: "nowrap", textTransform: "uppercase"
  }),
};

// ─── RISK & TYPE METADATA ────────────────────────────────────────────────
const RISK_M = { 1: { l: "Low", c: C.green }, 2: { l: "Medium", c: C.amber }, 3: { l: "High", c: "#fb923c" }, 4: { l: "Critical", c: C.red } };
const TYPE_M = { install: "#38a8ff", configure: "#ffb300", verify: "#00ff88", test: "#ff4f8b", document: "#c084fc" };

// ═══════════════════════════════════════════════════════════════════════
// 13 IMPLEMENTATION DOMAINS (Complete)
// ═══════════════════════════════════════════════════════════════════════
export const E2E_DOMAINS = [
  {
    id: "DOM-01", seq: 1, color: C.cyan, label: "Platform & Infrastructure",
    icon: "🖥", phase: "Pre-Phase 1", effortDays: 25,
    desc: "Server provisioning, OS hardening, network segregation (IT/OT DMZ), licence installation, and baseline smoke-tests for every Siemens Xcelerator node.",
    why: "Without Platform Setup → nothing runs. App.jsx assumes servers exist but they must be provisioned, hardened, and baseline-tested before any integration can begin.",
    items: [
      {
        id: "D01-I01", name: "TC 2512 Server Provisioning & Installation",
        tool: "TC Installer / RHEL 8.6", effort: 5, risk: 3,
        desc: "Install Teamcenter 2512 (Dispatcher, FMS, Indexing, Visualization) on RHEL 8.6 VMs with Oracle 19c RAC database.",
        tasks: [
          { id: "D01-I01-T1", type: "install", name: "Provision TC application and DB servers",
            dod: ["TC server VMs provisioned: 8vCPU 32GB RAM 500GB SSD each — confirmed in vSphere",
              "Oracle 19c RAC two-node cluster healthy: tnsping returns OK from TC host",
              "TC Dispatcher Server (TcServer) starts: port 7001 responds to HTTP GET /tc/health → 200",
              "TC FMS (File Management Server) accessible: fmsmaster port 4544 bound — netstat confirms",
              "TC Indexing Engine (Solr/Elastic) healthy: admin console shows 0 shard errors"] },
          { id: "D01-I01-T2", type: "configure", name: "Configure TC licences and base environment",
            dod: ["All TC licence strings imported: lmstat shows each feature checked out correctly",
              "TC Manufacturing module licence active: Mfg0BvrProcess object type createable",
              "TC Active Workspace (AWC) 6.x deployed: login page loads over HTTPS on port 443",
              "TC BMIDE (Business Modeller): custom types deployed to production server without errors",
              "TC Subscription Manager enabled: change event bus operational — test event fires within 30s"] },
          { id: "D01-I01-T3", type: "verify", name: "Baseline performance benchmark",
            dod: ["TC login-to-dashboard in ≤5 seconds on corporate LAN (measured 10× avg)",
              "TC SOA bulk item create (100 items): completes in ≤60 seconds — logged",
              "TC database backup completes nightly: backup job green in Oracle RMAN scheduler"] },
        ]
      },
      {
        id: "D01-I02", name: "Opcenter EX DS 2601 Server Installation",
        tool: "Opcenter Installer / Windows Server 2022", effort: 4, risk: 3,
        desc: "Install Opcenter EX DS 2601 on Windows Server 2022 with SQL Server 2019 Always On, IIS 10, and Opcenter REST API v1.",
        tasks: [
          { id: "D01-I02-T1", type: "install", name: "Install Opcenter EX DS 2601 + SQL Server 2019",
            dod: ["SQL Server 2019 Always On AG configured: primary + 1 secondary replica, RTO ≤ 30s",
              "Opcenter EX DS 2601 installed: Opcenter Studio opens without licence warning",
              "Opcenter REST API v1 endpoint: GET /opcenter/api/v1/ping → {status:OK version:2601}",
              "Opcenter IIS application pool: identity = OpcenterSvcUser (dedicated AD account)",
              "Opcenter event bus (RabbitMQ or built-in): queue depth = 0, broker healthy"] },
          { id: "D01-I02-T2", type: "configure", name: "Configure Opcenter licences and Quality add-on",
            dod: ["Opcenter EX DS licence applied: SPC, DataCollection, NonConformance modules visible",
              "Opcenter Quality add-on installed: SPC chart rendering active in Studio",
              "Opcenter APS licence applied: APS scheduling engine starts without error",
              "Opcenter REST API key management: ApiKey table in DB accessible, test key generates"] },
        ]
      },
      {
        id: "D01-I03", name: "SAP S/4HANA 2023 On-Premises Baseline Check",
        tool: "SAP Basis / SAP SE38", effort: 3, risk: 2,
        desc: "Validate SAP S/4HANA 2023 on-premises instance readiness: PP, QM, PM, MM, CO modules active; ALE/iDoc stack healthy; RFC destinations reachable.",
        tasks: [
          { id: "D01-I03-T1", type: "verify", name: "Confirm S/4HANA module activation and ALE readiness",
            dod: ["SAP SM50/SM66: all work processes GREEN, no PRIV mode hangs",
              "PP, QM, PM, MM, CO modules active: CO01 QA32 IW21 ME21N accessible",
              "SAP ALE: BD54 logical systems defined, WE20 partner profiles framework ready",
              "SAP WE19 iDoc test: LOIPRO01 test iDoc transmits to test partner without WE02 error",
              "SAP SM59 RFC destinations: CN4S, AIG, EDI partner — all Connection Test = OK"] },
          { id: "D01-I03-T2", type: "configure", name: "Configure SAP plant structure for CLM site",
            dod: ["SAP SPRO: manufacturing plant PL01 configured with correct country/currency/calendar",
              "SAP PP: MRP area, MRP type PD configured for all in-scope materials",
              "SAP QM: Quality management active for plant PL01 — QA01 inspection origin 03 enabled",
              "SAP PM: Plant Maintenance plant configured, equipment categories defined"] },
        ]
      },
      {
        id: "D01-I04", name: "CN4T / CN4S / AIG / IEM Middleware Installation",
        tool: "Windows Server 2022 / Linux Ubuntu 22.04", effort: 5, risk: 3,
        desc: "Install and baseline-test all four integration middleware components: CN4T (TC↔MES), CN4S (SAP↔MES), AIG (TC↔SAP), Industrial Edge Management.",
        tasks: [
          { id: "D01-I04-T1", type: "install", name: "Install CN4T + CN4S on dedicated Windows integration server",
            dod: ["CN4T Windows service: Status=Running StartType=Automatic — Services.msc confirms",
              "CN4S Windows service: Status=Running StartType=Automatic — Services.msc confirms",
              "CN4T admin HTTPS console: https://{server}:8443/cn4t/admin → loads without error",
              "CN4S admin HTTPS console: https://{server}:8445/cn4s/admin → loads without error",
              "Both services auto-restart on crash: sc failure CN4T reset=3600 actions=restart/5000"] },
          { id: "D01-I04-T2", type: "install", name: "Install AIG Rule Engine and Industrial Edge Management (IEM)",
            dod: ["AIG server installed on Ubuntu 22.04: systemctl status aig → active (running)",
              "AIG admin UI: http://{aig-server}:9090/admin → login page loads",
              "AIG TC adapter, SAP adapter, EDI adapter licences installed — no WARN in AIG startup log",
              "IEM installed: https://{iem-server} → IEM dashboard loads, fleet management accessible",
              "IEM connected to Insights Hub tenant: Fleet Manager shows Connected status"] },
        ]
      },
      {
        id: "D01-I05", name: "Network Architecture: OT/IT DMZ Firewall Rules",
        tool: "Network Firewall / Cisco / Palo Alto", effort: 4, risk: 4,
        desc: "Implement Purdue Model network segmentation: IT (Level 4/3) ↔ DMZ ↔ OT (Level 2/1/0). Define and validate all required firewall rules for CLM data flows.",
        tasks: [
          { id: "D01-I05-T1", type: "configure", name: "Implement Purdue Model network zones and firewall rules",
            dod: ["Network zones created: IT-LAN (L4), Integration-DMZ (L3.5), OT-LAN (L2), Machine-LAN (L1)",
              "Firewall rule: TC (IT-LAN:7001) → CN4T (DMZ) → Opcenter (DMZ) — allowed, logged",
              "Firewall rule: SAP (IT-LAN:3300) → CN4S (DMZ) → Opcenter (DMZ) — allowed, logged",
              "Firewall rule: IEM (DMZ) ↔ IED devices (OT-LAN) — allowed on OPC-UA port 4840",
              "Firewall rule: IED (OT-LAN) → Hub (Internet:8883 MQTT/TLS) — allowed outbound only",
              "BLOCKED: direct IT-LAN ↔ Machine-LAN (L1) traffic — penetration test confirms block",
              "Firewall rules version-controlled in Git, change management process documented"] },
          { id: "D01-I05-T2", type: "test", name: "Network penetration test and latency baseline",
            dod: ["Penetration test report: 0 critical findings on OT/IT boundary (signed by security lead)",
              "OT LAN latency baseline: ping CNC machines from IED ≤1ms — documented",
              "IT LAN latency baseline: CN4T → Opcenter round-trip ≤5ms — documented",
              "VPN access for remote maintenance configured: MFA enforced, session recorded"] },
        ]
      },
    ],
  },
  {
    id: "DOM-02", seq: 2, color: C.amber, label: "TC Platform Configuration",
    icon: "⚙", phase: "Pre-Phase 2", effortDays: 18,
    desc: "Configure Teamcenter 2512 Manufacturing modules: MEProcess/MEOperation types, Plant Structure, Workflow templates, ECO lifecycle, Classification, and TC Subscription triggers.",
    why: "Without TC Configuration → BMIDE types don't exist; CN4T has nothing to sync. The entire CLM data model depends on correct TC type definitions.",
    items: [
      {
        id: "D02-I01", name: "TC Manufacturing Types & BMIDE Configuration",
        tool: "TC BMIDE / TC Rich Client", effort: 4, risk: 3,
        desc: "Define and deploy all TC Manufacturing business object types needed for CLM: MEWorkpackage, MEProcess, Mfg0BvrOperation subtypes, BOE types, QC attributes.",
        tasks: [
          { id: "D02-I01-T1", type: "configure", name: "Define Manufacturing BO types in TC BMIDE",
            dod: ["Mfg0BvrOperation subtypes deployed: Assembly, Machining, Inspection, Test, QC_Hold, Rework",
              "MEWorkpackage type with custom attributes: effectivity_date, site_code, release_status",
              "MEEquipmentRevision subtypes: Machine, Tool, Gauge, Fixture — all createable in TC",
              "UserData attributes added to Mfg0BvrOperation: std_time_min, qc_hold, ergonomics_reba_score",
              "BMIDE deployment package pushed to production TC without errors: 0 failed type registrations"] },
          { id: "D02-I01-T2", type: "configure", name: "Configure TC Plant Structure and WorkCenter hierarchy",
            dod: ["TC Plant Structure: Site → Building → Production Line → WorkCenter — hierarchy matches EasyPlan",
              "All WorkCenters created in TC: IDs match EasyPlan station names and SAP work center IDs",
              "TC WorkCenter attributes: shift_model, capacity_hours, cost_center_id — all populated",
              "TC Mfg App views configured: BOP tree, BOE tree, Line Balance summary view operational"] },
        ]
      },
      {
        id: "D02-I02", name: "TC ECO / Change Management Lifecycle",
        tool: "TC Workflow Designer / TC Change Mgmt", effort: 4, risk: 3,
        desc: "Configure TC ECR→ECO lifecycle with approval workflow, effectivity management, and CN4T/AIG subscription triggers for downstream propagation.",
        tasks: [
          { id: "D02-I02-T1", type: "configure", name: "Define ECR→ECO workflow with approval routing",
            dod: ["ECR lifecycle: Draft → Submitted → Review → Approved → Rejected states defined",
              "ECO lifecycle: Draft → Submitted → Approved → Released → Superseded states defined",
              "ECO approval workflow: Engineering Manager → Manufacturing Manager → QA sign-off",
              "Effectivity management: date-effective and unit-effective both supported on TC BOM",
              "TC Subscription: ECO Released state-change triggers CN4T and AIG sync events — tested"] },
          { id: "D02-I02-T2", type: "test", name: "End-to-end ECO lifecycle test",
            dod: ["ECR created → routed to reviewers → approved by all parties in correct sequence",
              "ECO released: downstream sync fired within 60s (CN4T event log + AIG event log both show ECO trigger)",
              "Affected items report on ECO: shows all impacted BOMs, BOPs, BOEs correctly",
              "TC audit trail: every state change records user, timestamp, and comments — no gaps"] },
        ]
      },
      {
        id: "D02-I03", name: "TC User Roles, Teams & Access Control",
        tool: "TC Organization / TC Access Manager", effort: 3, risk: 2,
        desc: "Define TC teams, roles, and access control rules for all CLM stakeholder groups.",
        tasks: [
          { id: "D02-I03-T1", type: "configure", name: "Configure TC roles and access control for CLM",
            dod: ["TC roles created: PLM_Engineer, Mfg_Engineer, IE_Engineer, Quality_Engineer, CN4T_Service, AIG_Service",
              "TC Access Manager rules: each role can only read/write the object types relevant to their function",
              "CN4T service account: mfg_bop_read + item_read only (no write access to engineering objects)",
              "AIG service account: item_read + bom_read only for TC adapter",
              "Named user licences assigned: licence count verified against procurement order",
              "AD group sync: TC roles mapped to AD groups — user added to AD group gets TC role within 1h"] },
        ]
      },
      {
        id: "D02-I04", name: "TC Classification & Attribute Libraries",
        tool: "TC Classification / TC Admin", effort: 3, risk: 2,
        desc: "Configure TC Classification (ICM) for materials, equipment, and process standards. Include industry-specific material class attributes (NACE, API, pressure class).",
        tasks: [
          { id: "D02-I04-T1", type: "configure", name: "Set up TC Classification library for materials and equipment",
            dod: ["TC Classification: Material class hierarchy — Raw Material → Forgings → Valve Bodies → Seats",
              "Industry-specific attributes in classification: API_Rating, NACE_Zone, H2S_Rating, Pressure_Class, Temperature_Class",
              "Equipment classification: Machine class hierarchy — CNC Machining Center → Lathe → Mill",
              "Classification views configured in TC Mfg Application: engineer can classify new items from Mfg App",
              "SAP material type mapping to TC classification: confirmed via AIG lookup table"] },
        ]
      },
      {
        id: "D02-I05", name: "TC Subscription Manager & Event Bus",
        tool: "TC Subscription Manager / TC Admin", effort: 4, risk: 3,
        desc: "Configure TC Subscription Manager to fire integration events to CN4T, CN4S, and AIG on the correct TC object state changes.",
        tasks: [
          { id: "D02-I05-T1", type: "configure", name: "Define and test all TC subscription triggers for CLM integration",
            dod: ["Subscription 1: MEWorkpackage Released → CN4T full BOP sync → Opcenter Spec created",
              "Subscription 2: ECO Released → CN4T delta sync + AIG ECO push — both fire within 60s",
              "Subscription 3: Mfg0BvrOperation attribute change (std_time_min) → CN4T delta push",
              "Subscription 4: ItemRevision Released → AIG material master create/update in SAP",
              "Subscription 5: MEEquipmentRevision Released → CN4T BOE push to Opcenter Resources",
              "Dead-letter handling: failed subscription events logged to TC Subscription Manager DLQ — visible in admin",
              "Subscription retry: failed events retried 3× with exponential backoff (30s, 2min, 10min)"] },
        ]
      },
    ],
  },
  {
    id: "DOM-03", seq: 3, color: C.green, label: "Opcenter Platform Configuration",
    icon: "🏭", phase: "Pre-Phase 2", effortDays: 14,
    desc: "Configure Opcenter EX DS 2601 platform: Sites, Resource Groups, Shift Calendars, Container Types, User Roles, DataCollection field libraries, SPC profiles, and NCR classification trees.",
    why: "Without Opcenter Configuration → no Site, no Resources, no DataCollection library. The MES layer has nothing to execute against.",
    items: [
      {
        id: "D03-I01", name: "Opcenter Site, Resource Groups & Shift Calendars",
        tool: "Opcenter Administration / Opcenter Studio", effort: 4, risk: 2,
        desc: "Create production site, all resource groups (machines, workstations, tools), shift calendars (day/night/weekend), and link to EasyPlan capacity model.",
        tasks: [
          { id: "D03-I01-T1", type: "configure", name: "Create Opcenter Site with all Resource Groups and Shift Calendars",
            dod: ["Opcenter Site created: SiteCode, SiteName, Timezone — all correct",
              "Resource Groups created for all in-scope machines: MACH-CNC-001 through MACH-CNC-020",
              "Resource Groups created for assembly stations: ASSY-STN-001 through ASSY-STN-010",
              "Shift calendars defined: DAYSHIFT (06:00-14:00), NIGHTSHIFT (22:00-06:00), WEEKEND-OFF",
              "Calendar linked to each Resource Group: machine availability calendar matches EasyPlan model",
              "Opcenter APS: machine capacity parameters = EasyPlan-exported values (from IF-016)"] },
          { id: "D03-I01-T2", type: "configure", name: "Configure Container Types and serial/batch tracking rules",
            dod: ["Container Type: SERIAL — 1 unit per container, unique serial number mandatory",
              "Container Type: BATCH — N units per container, batch number mandatory",
              "Container Type: ASSEMBLY — tracks sub-assembly with component genealogy",
              "Serial format rule: {SITE}-{YEAR}{WEEK}-{SEQ:06} — validated by Opcenter regex",
              "Batch format rule: BATCH-{YEAR}{MONTH}-{MAT:8} — validated",
              "Container status flow: Queued → In-Process → QC-Hold → Rework → Finished / Scrapped defined"] },
        ]
      },
      {
        id: "D03-I02", name: "Opcenter DataCollection Field Library",
        tool: "Opcenter Studio / Quality Administration", effort: 3, risk: 2,
        desc: "Build the Opcenter DataCollection field library covering all in-process and final inspection characteristics (dimensional, process, attribute).",
        tasks: [
          { id: "D03-I02-T1", type: "configure", name: "Create DataCollection field library from Control Plan",
            dod: ["DC fields created for all characteristics in APQP Control Plan: minimum 30 fields",
              "Variable characteristics: Bore_Diameter_mm, Surface_Finish_Ra, Hardness_HRC — each with USL/LSL from drawing",
              "Attribute characteristics: Leak_Test_Pass (OK/NOK), Hydrostatic_Test_Pass (OK/NOK)",
              "Industry-specific fields: NACE_Hardness_HRC (USL=22 per MR0175), Shell_Test_Pressure_bar, Seat_Test_N2_bar",
              "SPC-enabled fields flagged: SPC=true for all variable characteristics with Cpk target ≥1.33",
              "DC field names match SAP QM characteristic short texts exactly (case-sensitive — verified)"] },
        ]
      },
      {
        id: "D03-I03", name: "Opcenter NCR Classification Tree & CAPA Framework",
        tool: "Opcenter Quality Studio", effort: 3, risk: 2,
        desc: "Configure Opcenter NonConformance classification tree, defect codes, CAPA routing, and CN4S mapping to SAP QM notification types.",
        tasks: [
          { id: "D03-I03-T1", type: "configure", name: "Build NCR classification tree and defect code library",
            dod: ["Defect classification levels: Class (Internal/Customer/Supplier) → Category → Sub-type",
              "Industry-specific defect codes: NACE_HARDNESS_EXCEED, HYDRO_FAIL, SEAT_LEAK, DIMENSIONAL_OOT, SURFACE_DEFECT",
              "CN4S mapping: Internal→Q2, Customer Complaint→Q1, Supplier Defect→Q3 — configured and tested",
              "CAPA workflow: NCR Opened → QC Review → Root Cause → Corrective Action → Effectiveness Verify → Closed",
              "8D report template linked to Q1 customer complaint notifications — accessible from NCR in Opcenter"] },
        ]
      },
      {
        id: "D03-I04", name: "Opcenter User Roles & Permission Matrix",
        tool: "Opcenter Administration", effort: 2, risk: 2,
        desc: "Define Opcenter user roles, permission matrices, and AD group synchronisation for all shop floor and engineering personas.",
        tasks: [
          { id: "D03-I04-T1", type: "configure", name: "Configure Opcenter roles and AD group sync",
            dod: ["Opcenter roles: Operator, TeamLeader, QCInspector, MaintenanceTech, MESAdmin, ViewerReadOnly",
              "Permission matrix: Operator cannot disposition NCRs; QCInspector cannot modify Specs — verified",
              "AD group sync: Opcenter role = AD group membership, sync interval = 15 minutes",
              "SFC terminal accounts: shared operator login + individual RFID badge override for quality steps",
              "API service accounts: CN4T_API, CN4S_API, AIG_API — each with minimum required permissions only"] },
        ]
      },
    ],
  },
  {
    id: "DOM-04", seq: 4, color: C.gold, label: "Security & PKI Infrastructure",
    icon: "🔐", phase: "Pre-Phase 1", effortDays: 10,
    desc: "Design and implement the full security stack: PKI certificate hierarchy, OPC-UA security mode, API key management, audit logging, RBAC, and OT cybersecurity controls (IEC 62443).",
    why: "Without Security/PKI → OPC-UA connections fail; MQTT broker rejects IEDs. The entire OT/IT integration security layer must be in place before any machine connection.",
    items: [
      {
        id: "D04-I01", name: "PKI Certificate Hierarchy for OT/IT Integration",
        tool: "Microsoft CA / OpenSSL / CertManager", effort: 3, risk: 4,
        desc: "Establish a two-tier PKI (Root CA + Issuing CA) for all CLM components: CN4T HTTPS, Opcenter HTTPS, OPC-UA SignAndEncrypt, MQTT/TLS, and IED device certificates.",
        tasks: [
          { id: "D04-I01-T1", type: "configure", name: "Deploy Root CA + Issuing CA for CLM certificate hierarchy",
            dod: ["Offline Root CA installed on air-gapped server: Root CA cert validity 20 years",
              "Online Issuing CA deployed: issues certs for all CLM components automatically via ACME or auto-enroll",
              "Certificate inventory document: all issued certs listed with CN, expiry, renewal date — in Git",
              "Auto-renewal configured: certs renew at 75% of lifetime — email alert at 30 days before expiry",
              "OPC-UA PKI store configured on each IED: trusted certs folder, rejected certs folder — UA Security mode = Sign and Encrypt on all machines"] },
          { id: "D04-I01-T2", type: "verify", name: "Penetration test and certificate validity sweep",
            dod: ["All CLM HTTPS endpoints: TLS 1.2 minimum, no TLS 1.0 or 1.1 — confirmed by nmap ssl-enum-ciphers",
              "OPC-UA connection with anonymous mode: UAExpert fails to connect (anonymous blocked)",
              "OPC-UA connection with valid cert: UAExpert connects and browses address space",
              "MQTT broker: TLS client certificate required — connection without cert rejected (RC=5 Not Authorized)"] },
        ]
      },
      {
        id: "D04-I02", name: "IEC 62443 OT Cybersecurity Controls",
        tool: "Network Firewall / SIEM / Endpoint Protection", effort: 3, risk: 4,
        desc: "Implement IEC 62443 SL2 security level for OT network: zone/conduit model, intrusion detection, patch management for OT devices, and incident response plan.",
        tasks: [
          { id: "D04-I02-T1", type: "configure", name: "Implement IEC 62443 SL2 controls for OT zone",
            dod: ["Zone/conduit model documented and implemented: matches IEC 62443-3-3 SL2 requirements",
              "OT SIEM (Claroty or Dragos): passive monitoring on OT LAN — no active scanning on machine network",
              "Patch management policy for IED firmware: tested in staging before OT deployment — SOC approval required",
              "Incident response plan for OT: playbook for ransomware, HMI compromise, rogue device — table-top exercise completed",
              "USB port lockdown on all IED units: only approved USB devices whitelisted",
              "Remote access for vendors: jump server with session recording — no direct machine access"] },
        ]
      },
      {
        id: "D04-I03", name: "API Key Management & Secret Rotation",
        tool: "HashiCorp Vault / Azure Key Vault", effort: 2, risk: 3,
        desc: "Centralise all CLM API keys, passwords, and integration secrets in a vault with automatic rotation and audit logging.",
        tasks: [
          { id: "D04-I03-T1", type: "configure", name: "Deploy secret vault and integrate with CLM components",
            dod: ["HashiCorp Vault (or Azure Key Vault) deployed and accessible from integration DMZ",
              "All CLM secrets stored in vault: CN4T TC password, Opcenter API key, SAP RFC password, Hub client secret",
              "CN4T, CN4S, AIG retrieve secrets from vault at startup — no plaintext passwords in config files",
              "Secret rotation: Opcenter API key rotated every 90 days — automated, zero downtime",
              "Vault audit log: every secret access logged with service identity, timestamp — exported to SIEM"] },
        ]
      },
      {
        id: "D04-I04", name: "Compliance Audit Logging (21 CFR Part 11 / GDPR readiness)",
        tool: "Opcenter Audit / TC Audit / SAP Audit", effort: 2, risk: 2,
        desc: "Configure audit trails across TC, Opcenter, SAP, and Edge to meet regulatory requirements and GDPR data minimisation.",
        tasks: [
          { id: "D04-I04-T1", type: "configure", name: "Enable and verify end-to-end audit logging",
            dod: ["TC audit log: every object create/modify/delete logged — TC Archive Manager retention 7 years",
              "Opcenter audit log: every Container move, DC entry, NCR status change — immutable audit trail",
              "SAP audit log: SM18 retention configured, SAP Security Audit Log active for production client",
              "Edge/Hub audit: all API calls to Hub Asset API logged with asset ID, user, timestamp",
              "GDPR: PII fields identified and pseudonymised in analytics exports (operator names → operator IDs)"] },
        ]
      },
    ],
  },
  {
    id: "DOM-05", seq: 5, color: C.teal, label: "ISA-95 / ISA-88 Compliance Layer",
    icon: "📐", phase: "Pre-Phase 2", effortDays: 8,
    desc: "Map the CLM implementation to ISA-95 (IEC 62264) functional hierarchy and ISA-88 batch control model. Ensure B2MML compliance for MES↔ERP information exchange and define the Work Definition hierarchy.",
    why: "Without ISA-95/88 compliance → audit bodies and regulators reject the system. Manufacturing execution standards are mandatory for regulated industries.",
    items: [
      {
        id: "D05-I01", name: "ISA-95 Level Mapping & B2MML Schema",
        tool: "Architecture Document / XML Schema", effort: 3, risk: 2,
        desc: "Map each CLM system to the correct ISA-95 level and validate that all MES↔ERP exchanges conform to the B2MML (Business To Manufacturing Markup Language) schema.",
        tasks: [
          { id: "D05-I01-T1", type: "document", name: "Document ISA-95 system level mapping",
            dod: ["ISA-95 mapping document completed and approved by architect:",
              "Level 4: SAP S/4HANA (ERP) — Business Planning and Logistics",
              "Level 3: Opcenter EX DS (MES) — Manufacturing Operations Management",
              "Level 3.5: CN4T, CN4S, AIG (Integration) — Site Manufacturing Operations",
              "Level 2: SFC, EasyPlan (Control) — Area Supervisory Control",
              "Level 1: TIA Portal S7-1500, OPC-UA (Control) — Basic Control",
              "Level 0: CNC machines, sensors, CMM (Field) — Physical Process",
              "Document signed off by: Solution Architect, IT Manager, OT Manager, Customer"] },
          { id: "D05-I01-T2", type: "configure", name: "Validate B2MML schema compliance for MES↔ERP exchanges",
            dod: ["ProductionOrder B2MML: SAP production order → Opcenter WO mapping documented against B2MML WorkOrder schema",
              "MaterialLot B2MML: Opcenter Container/Lot maps to B2MML MaterialLot element — diagram in architecture doc",
              "WorkDefinition B2MML: Opcenter Spec (Product+BOM+Workflow) = B2MML WorkDefinition hierarchy confirmed",
              "PersonnelDefinition B2MML: Opcenter user roles mapped to B2MML OperationsCapability hierarchy",
              "XML validation: sample MES↔ERP payload validated against B2MML XSD schema — 0 validation errors"] },
        ]
      },
      {
        id: "D05-I02", name: "ISA-88 Batch Control Model for Manufacturing",
        tool: "Process Model Document / Opcenter", effort: 2, risk: 1,
        desc: "Apply ISA-88 batch control hierarchy to discrete manufacturing: map assembly process to Procedure → Unit Procedure → Operation → Phase model.",
        tasks: [
          { id: "D05-I02-T1", type: "document", name: "Create ISA-88 process model for manufacturing assembly",
            dod: ["ISA-88 hierarchy documented for assembly process:",
              "Procedure: Complete Product Assembly",
              "Unit Procedures: Body Machining → Seat Lapping → Assembly → Pressure Test → Final Inspection",
              "Operations: per unit procedure — matches Opcenter WorkflowStep breakdown",
              "Phases: per operation — matches EasyPlan MTM operation elements",
              "Equipment model: Physical Model (Machine) + Procedural Control Model (Opcenter SFC) documented",
              "Document reviewed by: Process Engineer + Manufacturing Engineer — signed off"] },
        ]
      },
      {
        id: "D05-I03", name: "Work Definition Hierarchy (ISA-95 Level 3)",
        tool: "Opcenter Studio", effort: 3, risk: 2,
        desc: "Implement the ISA-95 Work Definition hierarchy in Opcenter: WorkMaster → Segment Requirements → Parameter Requirements, ensuring full traceability to TC BOP.",
        tasks: [
          { id: "D05-I03-T1", type: "configure", name: "Configure Opcenter Work Definition hierarchy aligned to ISA-95",
            dod: ["Opcenter Spec = ISA-95 WorkDefinition — naming convention: {ProductPN}-{RevLevel}-SPEC",
              "Opcenter Workflow = ISA-95 OperationsDefinition — steps named as {OP_SEQ}-{OP_NAME}",
              "Opcenter DataCollection Spec = ISA-95 PhysicalDefinition and TestDefinition — confirmed",
              "Opcenter BOM = ISA-95 MaterialDefinition and MaterialRequirement hierarchy",
              "Traceability: each Opcenter Spec references source TC WorkPackage ID in custom attribute TC_WP_ID"] },
        ]
      },
    ],
  },
  {
    id: "DOM-06", seq: 6, color: C.purple, label: "Master Data Migration",
    icon: "📦", phase: "Pre-Go-Live", effortDays: 30,
    desc: "Migrate all master data from legacy systems or greenfield creation: TC Items/BOMs/BOPs, SAP Material Masters, Opcenter Products/Specs, Equipment Masters, and validate 100% completeness.",
    why: "Without Master Data Migration → Specs, BOMs, Products don't exist on Day 1. The entire CLM system has nothing to process.",
    items: [
      {
        id: "D06-I01", name: "TC Master Data Migration (Items, BOMs, BOPs)",
        tool: "TC Import/Export Wizard / Python Migration Scripts", effort: 8, risk: 4,
        desc: "Migrate existing product structures (EBOM, MBOM, BOP, BOE) into Teamcenter from source systems.",
        tasks: [
          { id: "D06-I01-T1", type: "configure", name: "Extract, transform, and load TC master data",
            dod: ["Data extraction complete: all in-scope Items extracted from source system — count verified vs manifest",
              "Data transformation: TC item types, attribute values, UOM codes — all mapped per data model",
              "TC import: 0 import errors; all Items in Released state with correct revision",
              "EBOM structures complete: parent-child relationships correct — equivalent BOM explosion verified",
              "MBOM structures complete: manufacturing BOM linked to EBOM via MEWorkpackage",
              "BOP operations complete: all Mfg0BvrOperation records with std_time_min from EasyPlan MTM",
              "Data quality check: 0 items with null mandatory attributes"] },
          { id: "D06-I01-T2", type: "verify", name: "TC master data completeness and accuracy validation",
            dod: ["Random sample validation: 50 items spot-checked vs source system or drawing — 100% match",
              "BOM level count verified: 0 orphaned components",
              "TC Classification: all migrated items classified correctly — 0 unclassified items",
              "TC-to-Opcenter sync test: migrate 10 representative items through CN4T → Opcenter Spec created for each",
              "Migration sign-off: Customer PLM Lead + SI Lead sign data quality acceptance"] },
        ]
      },
      {
        id: "D06-I02", name: "SAP Master Data Migration (Materials, Routings, Equipment)",
        tool: "SAP LSMW / SAP MDG / BAPI / Python", effort: 8, risk: 4,
        desc: "Create or migrate SAP Material Masters, PP Routings, Equipment Masters, and QM Inspection Plans in SAP S/4HANA aligned to TC master data.",
        tasks: [
          { id: "D06-I02-T1", type: "configure", name: "Load SAP Material Masters via BAPI_MATERIAL_SAVEDATA",
            dod: ["All in-scope SAP Material Masters created: MTART, MEINS, MARA, MARD plant data all populated",
              "BAPI_MATERIAL_SAVEDATA load: 0 error records in import log",
              "Material descriptions match TC item_desc (within 40-char SAP limit) — spot-check 50 materials",
              "MRP data: DISMM=PD, DISPO=001 (MRP controller) for all HALB and FERT materials",
              "Batch classification: industry-specific classes assigned — Heat_Number, COC_Number, Supplier_Cert attributes ready"] },
          { id: "D06-I02-T2", type: "configure", name: "Load SAP PP Routings with EasyPlan standard times",
            dod: ["SAP routings created via CA01 BAPI for all manufactured materials",
              "EasyPlan MTM standard times loaded into SAP VGW01/VGW03 fields (÷60 for hours)",
              "Work center assignments on routing operations match TC WorkCenter→SAP mapping table",
              "SAP CK11N preliminary cost estimate runs without error for all materials — costs plausible",
              "Routing validity dates: Valid-From = CLM go-live date"] },
          { id: "D06-I02-T3", type: "configure", name: "Create SAP PM Equipment Masters for all in-scope machines",
            dod: ["SAP PM Equipment Masters created for all 20+ in-scope machines (IE01)",
              "Equipment number matches IEM device ID and Hub Asset ID — cross-reference table in Git",
              "Functional Locations created: Plant → Building → Production Line → Machine",
              "Maintenance plans (IP01): preventive maintenance schedules loaded for critical machines",
              "Equipment classification: Machine_Type, OPC_UA_Endpoint, IED_Serial in SAP class attrs"] },
        ]
      },
      {
        id: "D06-I03", name: "Opcenter Master Data Bootstrap (Products, Specs, Resources)",
        tool: "Opcenter REST API / Python Bootstrap Scripts", effort: 6, risk: 3,
        desc: "Bootstrap Opcenter with Products, Specs, BOMs, Workflows, Resources, DataCollection Specs for all in-scope materials — prior to CN4T going live.",
        tasks: [
          { id: "D06-I03-T1", type: "configure", name: "Bootstrap Opcenter via REST API for all in-scope products",
            dod: ["Opcenter Products created for all in-scope materials (min 100+): via POST /products batch script",
              "Opcenter Specs created for all manufactured products: Product + BOM + Workflow all linked",
              "Opcenter DataCollection Specs linked to appropriate Workflows via DCSpec assignment",
              "Resources created for all machines: ResourceGroup, Calendar, MaxCapacity from EasyPlan",
              "Bootstrap script idempotent: re-run produces no duplicates — tested 3× in staging",
              "Bootstrap completion report: 0 failed records, all counts match expected from master data list"] },
        ]
      },
      {
        id: "D06-I04", name: "Data Quality Framework & Golden Record Management",
        tool: "Data Quality Tool / Excel / Python", effort: 4, risk: 3,
        desc: "Establish data quality rules, golden record arbitration for multi-system item master, and ongoing data governance process.",
        tasks: [
          { id: "D06-I04-T1", type: "document", name: "Define data quality rules and golden record ownership",
            dod: ["Golden record policy documented: TC is system of record for product definition; SAP for financial; Opcenter for actual production",
              "Data quality rules defined: 15 rules covering mandatory attributes, format validation, referential integrity",
              "Automated DQ checks: Python script runs nightly against TC API and Opcenter REST — DQ report emailed",
              "DQ score target: ≥98% for all mandatory attributes populated, ≥95% for optional attributes",
              "Data steward assigned per domain: TC data steward (PLM team), SAP data steward (ERP team), Opcenter data steward (MES team)"] },
        ]
      },
    ],
  },
  {
    id: "DOM-07", seq: 7, color: C.pink, label: "Business Process Redesign",
    icon: "🔄", phase: "Pre-Phase 1", effortDays: 20,
    desc: "Document As-Is processes, design To-Be CLM processes, conduct value stream mapping (VSM), SIPOC analysis, and confirm process acceptance before technical configuration begins.",
    why: "Without Process Redesign → technology installed but processes unchanged = no ROI. CLM is a business transformation, not just a technology project.",
    items: [
      {
        id: "D07-I01", name: "As-Is Process Mapping & Pain Point Analysis",
        tool: "Visio / Lucidchart / Workshop Facilitation", effort: 5, risk: 1,
        desc: "Conduct structured workshops with each functional team to document current processes, identify manual handoffs, data rework, and lead time waste.",
        tasks: [
          { id: "D07-I01-T1", type: "document", name: "Complete As-Is process maps for all 8 CLM functional areas",
            dod: ["As-Is process maps completed for: Engineering Change, Production Planning, Shop Floor Execution, Quality Management, Material Management, Maintenance, Reporting, Supplier Management",
              "Pain point register: ≥3 pain points identified per functional area — signed off by functional leads",
              "Manual handoff count in As-Is: quantified — baseline established",
              "Average ECO cycle time As-Is: measured and documented — baseline for CLM ROI",
              "Workshop attendance: ≥80% attendance by key stakeholders in each session — sign-off sheets filed"] },
        ]
      },
      {
        id: "D07-I02", name: "To-Be Process Design & CLM Value Stream Map",
        tool: "Visio / EasyPlan VSM / Workshop", effort: 6, risk: 2,
        desc: "Design future-state (To-Be) processes enabled by CLM integration. Produce a Value Stream Map showing information flow across TC → EasyPlan → Opcenter → SFC → SAP → Hub.",
        tasks: [
          { id: "D07-I02-T1", type: "document", name: "Complete To-Be VSM and SIPOC for all CLM processes",
            dod: ["To-Be VSM completed: information flow across all 7 CLM pipelines mapped with system, trigger, frequency, and SLA",
              "SIPOC completed for: BOP Change Process, Production Order Execution, Quality NCR Process, Predictive Maintenance",
              "Lead time improvement: To-Be ECO cycle time target documented — accepted by customer",
              "Automation rate improvement: manual data entries targeted to reduce by ≥80% — tracked in benefits register",
              "Process acceptance: Customer Operations Director signs off To-Be process maps before configuration starts"] },
          { id: "D07-I02-T2", type: "document", name: "Define CLM SLAs and KPIs",
            dod: ["SLA register defined: CN4T sync ≤90s, WO dispatch ≤30s, NCR→SAP notification ≤2min, Edge anomaly→hold ≤30s",
              "KPI dashboard design: OEE, First Pass Yield, ECO Cycle Time, On-Time Delivery, Predictive Maintenance Hit Rate",
              "KPI baselines: current values measured from legacy systems — documented in benefits register",
              "KPI targets: customer agrees improvement targets per KPI — signed in project charter addendum",
              "KPI data sources mapped: each KPI → source system → Insights Hub aggregation → dashboard field"] },
        ]
      },
      {
        id: "D07-I03", name: "RACI Matrix for CLM Business Processes",
        tool: "RACI Tool / Excel / Confluence", effort: 3, risk: 1,
        desc: "Define Responsible, Accountable, Consulted, Informed (RACI) for every CLM business process and technical task.",
        tasks: [
          { id: "D07-I03-T1", type: "document", name: "Complete RACI for all CLM processes and technical integration tasks",
            dod: ["RACI matrix completed: covers 40+ process activities across 8 functional areas",
              "No process step without exactly 1 Accountable owner",
              "IT/OT split clear in RACI: OT team accountable for machine-level config; IT team accountable for server-level config",
              "RACI reviewed and signed by: Customer CLM Project Sponsor, SI Project Manager, functional leads",
              "RACI stored in project collaboration tool with version history"] },
        ]
      },
      {
        id: "D07-I04", name: "Standard Operating Procedures (SOPs) for CLM Operations",
        tool: "SOP Templates / Word / Confluence", effort: 6, risk: 2,
        desc: "Author and review SOPs for all new CLM-enabled processes: ECO execution, WO dispatch, NCR disposition, predictive maintenance response, and EasyPlan recalibration.",
        tasks: [
          { id: "D07-I04-T1", type: "document", name: "Author and approve all CLM SOPs prior to go-live",
            dod: ["SOP-01: Engineering Change Execution in TC (author: PLM team, approver: Engineering Manager)",
              "SOP-02: Production Order Dispatch and Shop Floor Execution (author: MES team, approver: Production Manager)",
              "SOP-03: NonConformance (NCR) Disposition and CAPA (author: Quality team, approver: Quality Manager)",
              "SOP-04: Predictive Maintenance Alert Response (author: Maintenance team, approver: Maintenance Manager)",
              "SOP-05: EasyPlan Monthly Recalibration (author: IE/Manufacturing Engineering team)",
              "SOP-06: CLM System Administration and Health Checks (author: IT team, approver: IT Manager)",
              "All SOPs: reviewed by functional leads, approved, and uploaded to DMS before go-live training begins"] },
        ]
      },
    ],
  },
  {
    id: "DOM-08", seq: 8, color: C.blue, label: "Testing Strategy",
    icon: "🧪", phase: "Phase: Test", effortDays: 30,
    desc: "Execute structured test phases: Unit Integration Tests (UIT), System Integration Tests (SIT), User Acceptance Tests (UAT), Performance & Load Tests, and Disaster Recovery (DR) Test.",
    why: "Without Testing (SIT/UAT) → go-live risk is unacceptable. Structured testing across all layers is the only way to ensure production readiness.",
    items: [
      {
        id: "D08-I01", name: "Unit Integration Testing (UIT) per Interface",
        tool: "Postman / SoapUI / CN4T Test Console", effort: 5, risk: 2,
        desc: "Execute UIT for each of the CLM interfaces in isolation: verify each DoD criterion with documented pass/fail evidence before proceeding to SIT.",
        tasks: [
          { id: "D08-I01-T1", type: "test", name: "UIT planning and execution for all interfaces",
            dod: ["UIT test plan approved: covers all DoD criteria across all interfaces — signed by Test Lead",
              "UIT environment: dedicated test TC/Opcenter/SAP instances with synthetic test data",
              "UIT execution: each interface tested independently before cross-interface tests",
              "UIT pass criteria: 100% DoD criteria passed for each interface before sign-off",
              "UIT defect log: all defects logged in JIRA with severity, owner, and resolution date",
              "UIT sign-off: SI Technical Lead signs UIT completion for each interface"] },
        ]
      },
      {
        id: "D08-I02", name: "System Integration Testing (SIT) — End-to-End Scenarios",
        tool: "SIT Environment / Test Management Tool", effort: 8, risk: 3,
        desc: "Execute SIT covering 12 end-to-end business scenarios spanning multiple interfaces and systems.",
        tasks: [
          { id: "D08-I02-T1", type: "test", name: "Execute 12 SIT end-to-end business scenarios",
            dod: ["SIT Scenario 1: New Product Introduction — TC Item → MBOM → BOP (EasyPlan) → CN4T → Opcenter Spec → SAP Material+Routing — PASS",
              "SIT Scenario 2: Engineering Change Order — TC ECO → CN4T delta → Opcenter Spec Rev B → AIG → SAP BOM validity update — PASS",
              "SIT Scenario 3: Production Order Execution — SAP CO02 → CN4S → Opcenter WO → SFC dispatch → step completion → SAP GR+CO — PASS",
              "SIT Scenario 4: Quality NCR — CMM Q-DAS → Opcenter SPC hold → NCR → CN4S → SAP QM Q2 notification → 8D — PASS",
              "SIT Scenarios 5-12: Edge AI, Predictive Maintenance, Supplier ASN, EasyPlan Recalibration, Scrap, Rework, Serial Genealogy, DNC Recovery — all PASS",
              "SIT defect count: 0 Critical, 0 High defects open at SIT sign-off"] },
        ]
      },
      {
        id: "D08-I03", name: "User Acceptance Testing (UAT)",
        tool: "UAT Environment / UAT Scripts / Customer Testers", effort: 8, risk: 3,
        desc: "Customer-executed UAT using production-representative data and processes.",
        tasks: [
          { id: "D08-I03-T1", type: "test", name: "Customer-executed UAT for all functional areas",
            dod: ["UAT participants: ≥2 testers per functional role",
              "UAT test cases executed: ≥30 end-to-end business scenarios covering all in-scope processes",
              "UAT pass criteria: ≥95% test cases PASS, 0 Critical defects open, all High defects have approved workaround",
              "UAT feedback incorporated: any usability issue logged and triaged before go-live",
              "Customer UAT sign-off: functional leads and Project Sponsor sign UAT Acceptance Certificate",
              "Regression test after defect fix: all fixed UAT defects re-tested and confirmed PASS"] },
        ]
      },
      {
        id: "D08-I04", name: "Performance & Load Testing",
        tool: "JMeter / Locust / CN4T Load Test Harness", effort: 5, risk: 3,
        desc: "Test CLM system performance under peak production load.",
        tasks: [
          { id: "D08-I04-T1", type: "test", name: "Execute performance and load tests for peak production scenarios",
            dod: ["Load test: 50 concurrent SFC users completing steps — Opcenter API P95 response ≤2 seconds",
              "CN4T sync throughput: 200 operation delta syncs per hour — CN4T log shows 0 timeouts",
              "SAP iDoc burst: 100 LOIPRO iDocs in 10 minutes — CN4S processes all within 30s each, 0 queue backup",
              "Hub timeseries ingestion: 50 machines × 5 signals × 1s sampling = 250 data points/second — Hub ingests without gap",
              "Opcenter DB performance: 8-hour production shift load test — DB CPU ≤60%, query P95 ≤500ms",
              "TC performance: 20 concurrent users running BOP edits — TC response P95 ≤3 seconds",
              "Performance test report signed by: SI Architect + Customer IT Manager"] },
        ]
      },
      {
        id: "D08-I05", name: "Disaster Recovery (DR) Test",
        tool: "DR Environment / Failover Scripts", effort: 4, risk: 3,
        desc: "Execute DR test for critical CLM components: Opcenter SQL failover, TC DB failover, CN4T service restart, and Edge offline mode validation.",
        tasks: [
          { id: "D08-I05-T1", type: "test", name: "Execute full DR test for all CLM critical components",
            dod: ["Opcenter SQL Always On failover: primary node killed → secondary promotes → Opcenter reconnects within RTO 30s",
              "TC Oracle failover: primary node fenced → secondary takes over → CN4T reconnects automatically within 60s",
              "CN4T service crash recovery: CN4T service killed → auto-restart within 30s → sync resumes — no data loss",
              "IED offline mode: IEM disconnected → IED continues OPC-UA data collection locally → reconnects → Hub receives buffered data",
              "SFC offline mode: Opcenter unreachable → SFC caches step completions → reconnects → all cached completions synced",
              "DR test report: RTO and RPO targets met for all components — signed by IT Manager + Customer",
              "DR runbook: step-by-step recovery procedures for each failure scenario — version-controlled in Git"] },
        ]
      },
    ],
  },
  {
    id: "DOM-09", seq: 9, color: C.amber, label: "Training & Change Management",
    icon: "🎓", phase: "Pre-Go-Live", effortDays: 20,
    desc: "Design and deliver role-based training for all CLM user personas. Execute organisational change management (OCM) to ensure adoption, reduce resistance, and sustain the change.",
    why: "Without Training → operators don't use SFC correctly; quality data is garbage. User adoption is the #1 cause of digital transformation failure.",
    items: [
      {
        id: "D09-I01", name: "Role-Based Training Programme",
        tool: "LMS / Classroom / Hands-on Simulated Environment", effort: 6, risk: 2,
        desc: "Design and deliver training for 8 user personas: Operator, Team Leader, QC Inspector, Manufacturing Engineer, PLM Engineer, SAP Planner, IT Admin, and Management.",
        tasks: [
          { id: "D09-I01-T1", type: "document", name: "Design training curriculum and materials for all CLM user roles",
            dod: ["Training needs analysis (TNA) completed: gap between current skills and CLM-required skills documented per role",
              "Training materials created for 8 roles: presentations, quick reference cards, e-learning modules, simulated exercises",
              "Operator training: SFC terminal operation (badge login, step completion, material scan, quality hold — ≤4 hours)",
              "QC Inspector training: NCR creation, SPC interpretation, quality hold release, SAP QM result entry (≤6 hours)",
              "Manufacturing Engineer training: EasyPlan MTM study, line balancing, REBA simulation, TC BOP export (≤16 hours)",
              "PLM Engineer training: TC ECO workflow, BMIDE type config, CN4T troubleshooting (≤8 hours)",
              "SAP Planner training: production order creation with CLM integration, APS schedule review, CO variance analysis (≤8 hours)",
              "IT Admin training: CN4T/CN4S administration, Opcenter admin, IEM management, certificate renewal (≤16 hours)"] },
          { id: "D09-I01-T2", type: "test", name: "Deliver training and assess competency",
            dod: ["All operators trained: ≥90% pass score on practical SFC assessment",
              "All QC Inspectors trained: ≥90% pass on NCR and SPC interpretation test",
              "Manufacturing Engineers trained: EasyPlan MTM study certification",
              "Training completion rate ≥90% of planned attendees across all roles before go-live",
              "Training effectiveness survey: ≥4.0/5.0 average rating across all sessions"] },
        ]
      },
      {
        id: "D09-I02", name: "Organisational Change Management (OCM)",
        tool: "OCM Framework (Prosci ADKAR) / Communications Plan", effort: 5, risk: 3,
        desc: "Execute structured OCM using ADKAR model: build Awareness, desire, knowledge, ability, reinforcement across all levels of the organisation.",
        tasks: [
          { id: "D09-I02-T1", type: "document", name: "Execute ADKAR OCM programme for CLM rollout",
            dod: ["Stakeholder analysis: 3×3 influence/impact matrix completed — all key stakeholders identified",
              "Communications plan: 12-week rollout communications schedule — emails, town halls, shop floor briefings",
              "Sponsor engagement: Executive Sponsor presents CLM vision to all affected departments before go-live",
              "Change champion network: 1 change champion per department — trained and active in Q&A sessions",
              "Resistance log: resistance sources identified, root causes addressed before go-live (completion ≥80%)",
              "ADKAR assessment: post-training survey confirms ≥80% of users reach Ability stage"] },
        ]
      },
      {
        id: "D09-I03", name: "Super User Programme & First-Line Support",
        tool: "Super User Training / Help Desk Setup", effort: 5, risk: 2,
        desc: "Train super users (2 per department) to provide first-line CLM support post-go-live, reducing dependency on SI team in hypercare.",
        tasks: [
          { id: "D09-I03-T1", type: "test", name: "Train and certify CLM super users for post-go-live support",
            dod: ["Super users identified: ≥2 per department (min 12 total across site)",
              "Super user training: 3-day advanced CLM training covering troubleshooting, basic config, escalation",
              "Super user certification: pass practical assessment (troubleshoot 5 simulated CLM issues within 30 min each)",
              "Knowledge base set up: ≥30 FAQ articles and how-to guides published to intranet before go-live",
              "Escalation process defined: Super User → SI L2 → Siemens L3 — response SLAs documented"] },
        ]
      },
    ],
  },
  {
    id: "DOM-10", seq: 10, color: C.red, label: "Cutover, Go-Live & Hypercare",
    icon: "🚀", phase: "Go-Live", effortDays: 15,
    desc: "Execute the phased production cutover: data freeze, final migration, system go-live sequence, production monitoring, and 4-week hypercare with defined rollback plan.",
    why: "Without Cutover Plan → data loss on go-live weekend; no rollback option. A structured, rehearsed cutover is mandatory for production confidence.",
    items: [
      {
        id: "D10-I01", name: "Cutover Plan & Data Migration Final Run",
        tool: "Cutover Checklist / Migration Scripts", effort: 5, risk: 4,
        desc: "Execute the final cutover: data freeze in legacy systems, final migration run, data validation, go-live sequence, and sign-off from each system owner.",
        tasks: [
          { id: "D10-I01-T1", type: "configure", name: "Execute cutover plan in correct system activation sequence",
            dod: ["Cutover weekend plan approved: hour-by-hour schedule with named owner for each step",
              "Legacy system data freeze: change freeze date communicated ≥2 weeks before cutover",
              "Final TC migration run: items in Released state, 0 errors in final migration log",
              "Final SAP migration run: Material Masters, Routings, Equipment Masters — reconciliation report 100% match",
              "Final Opcenter bootstrap: Products, Specs, Resources — spot-check 20 items, all correct",
              "Go-live sequence executed: Platform → Security → TC → Opcenter → CN4T → AIG → CN4S → SFC → Edge → Hub",
              "Go/No-Go checkpoint at each phase: functional lead confirms system healthy before next activation",
              "Cutover sign-off: all system owners sign Production Ready Declaration"] },
          { id: "D10-I01-T2", type: "document", name: "Define and test rollback plan",
            dod: ["Rollback triggers defined: ≥3 Critical defects open, or >10% production orders failing, or Opcenter unreachable >30 min",
              "Rollback plan documented: step-by-step revert to legacy system with data reconciliation",
              "Rollback rehearsal: executed in staging environment — confirmed ≤4 hours RTO for full rollback",
              "Rollback decision authority: Project Sponsor + Customer IT Manager + Operations Manager (unanimous required)",
              "Post-rollback recovery plan: root cause analysis → fix → re-cutover plan documented"] },
        ]
      },
      {
        id: "D10-I02", name: "Production Readiness Review (PRR) Checklist",
        tool: "PRR Checklist / Gate Review Meeting", effort: 3, risk: 4,
        desc: "Conduct formal Production Readiness Review gate before go-live: verify all technical, functional, training, and governance criteria are met.",
        tasks: [
          { id: "D10-I02-T1", type: "verify", name: "Execute Production Readiness Review (PRR) gate checklist",
            dod: ["PRR Technical Gate — ALL must be GREEN:",
              "✅ All CLM interfaces: 100% DoD criteria PASSED in UIT/SIT",
              "✅ SIT: 12/12 end-to-end scenarios PASSED, 0 Critical defects open",
              "✅ UAT: ≥95% test cases PASSED, Customer UAT Acceptance Certificate signed",
              "✅ Performance test: all SLA targets met, no outstanding performance defects",
              "✅ DR test: RTO/RPO met for all components",
              "PRR Training Gate — ALL must be GREEN:",
              "✅ Training completion rate ≥90% for all roles",
              "✅ Super users trained and certified (≥12 certified)",
              "✅ All CLM SOPs approved and uploaded to DMS",
              "PRR Governance Gate — ALL must be GREEN:",
              "✅ Master data migration: 100% completeness, data quality score ≥98%",
              "✅ Security: PKI deployed, penetration test passed, no open Critical security findings",
              "✅ Cutover plan approved by all stakeholders",
              "PRR Sign-off: Customer Project Sponsor + SI PM + Solution Architect all sign PRR declaration"] },
        ]
      },
      {
        id: "D10-I03", name: "Hypercare Support Programme (4 Weeks Post Go-Live)",
        tool: "Service Desk / Monitoring Dashboard / War Room", effort: 5, risk: 3,
        desc: "Provide 4-week hypercare: daily war room, dedicated SI support (on-site L1/L2, remote L3), system health monitoring, and weekly handover reviews.",
        tasks: [
          { id: "D10-I03-T1", type: "configure", name: "Set up hypercare war room and support structure",
            dod: ["Hypercare war room established: daily 08:00 stand-up (SI + Customer operational leads)",
              "SI on-site support: ≥2 SI engineers on site full-time for 4 weeks post go-live",
              "Support SLA hypercare: P1 (system down) response ≤15 min; P2 (functional defect) ≤2h; P3 (minor) ≤1 business day",
              "CLM health monitoring dashboard operational: all interfaces show green/amber/red status in real time",
              "Incident log: all issues logged, tracked, and reported in weekly hypercare report to sponsor",
              "Week 4 review: hypercare exit criteria met — defect count ≤5 open (all P3), super users handling L1 independently",
              "Hypercare exit report: signed by Customer IT Manager and SI PM — transitions to standard support contract"] },
        ]
      },
    ],
  },
  {
    id: "DOM-11", seq: 11, color: C.teal, label: "Monitoring & Observability",
    icon: "📡", phase: "Post-Go-Live", effortDays: 10,
    desc: "Deploy CLM-wide monitoring: interface health dashboards, alerting, SLA tracking, log aggregation, and a continuous improvement feedback loop.",
    why: "Without Monitoring → first production incident takes hours to diagnose. Observability is essential for production operations confidence.",
    items: [
      {
        id: "D11-I01", name: "CLM Interface Health Monitoring Dashboard",
        tool: "Insights Hub / Grafana / Prometheus / ELK Stack", effort: 4, risk: 2,
        desc: "Build a real-time CLM interface health dashboard showing status, last sync time, error count, and queue depth for all interfaces.",
        tasks: [
          { id: "D11-I01-T1", type: "configure", name: "Build CLM monitoring dashboard for all interfaces",
            dod: ["Monitoring agent deployed on each integration server: CN4T, CN4S, AIG, IEM — metrics scraped every 30s",
              "Dashboard panel per interface: Status (green/amber/red), last sync time, sync count (24h), error count (24h)",
              "Alert rules: CN4T error rate > 0 → PagerDuty/email alert to integration team within 5 minutes",
              "Alert rules: Opcenter queue depth > 100 → alert; Hub MQTT disconnected > 60s → alert",
              "Log aggregation: CN4T/CN4S/AIG logs shipped to ELK Stack (or Azure Sentinel) — searchable, 90-day retention",
              "SLA dashboard: tracks each integration SLA (sync latency vs target) — trend chart over 30 days"] },
          { id: "D11-I01-T2", type: "configure", name: "Configure automated SLA breach alert and escalation",
            dod: ["SLA breach alert: if CN4T sync latency P95 > 90s for 3 consecutive hours → P2 incident auto-created",
              "Escalation matrix: P1 → SI on-call + Customer IT Manager (SMS + call); P2 → SI integration team (email)",
              "Monthly SLA report: auto-generated from Grafana/Hub → emailed to CLM steering committee on 1st of month",
              "Trend analysis: error rate and latency trends flagged if 5-day moving average exceeds threshold"] },
        ]
      },
      {
        id: "D11-I02", name: "Continuous Improvement & CLM KPI Reporting",
        tool: "Insights Hub Analytics / Power BI / Hub Dashboards", effort: 3, risk: 1,
        desc: "Implement CLM KPI reporting: OEE, First Pass Yield, ECO cycle time, MTBF, predictive maintenance accuracy — reported to management monthly.",
        tasks: [
          { id: "D11-I02-T1", type: "configure", name: "Configure CLM KPI reporting suite",
            dod: ["Hub Analytics dashboard: OEE per machine (planned vs actual) — live, 7-day rolling, monthly trend",
              "First Pass Yield (FPY) dashboard: Opcenter SPC data → Hub Quality dashbard → daily FPY by product",
              "ECO Cycle Time report: TC ECR open date → TC ECO released date → average and trend",
              "Predictive Maintenance Accuracy: Hub ML model predictions vs actual failures — precision/recall tracked monthly",
              "Management report: auto-generated monthly PDF — distributed to steering committee",
              "Continuous improvement board: top 5 improvement opportunities identified from KPI data"] },
        ]
      },
      {
        id: "D11-I03", name: "Operational Runbook & Playbooks",
        tool: "Runbook Repository / Git / Confluence", effort: 3, risk: 1,
        desc: "Author and maintain operational runbooks for routine CLM administration, incident response, and scheduled maintenance tasks.",
        tasks: [
          { id: "D11-I03-T1", type: "document", name: "Complete and publish operational runbooks for CLM operations team",
            dod: ["Runbook-01: Daily CLM health check procedure (15 min morning check)",
              "Runbook-02: CN4T/CN4S service restart procedure (zero-data-loss restart sequence)",
              "Runbook-03: Certificate renewal procedure (annual for HTTPS, OPC-UA, MQTT certs)",
              "Runbook-04: Opcenter SQL failover procedure (manual failover, re-point applications, verify)",
              "Runbook-05: IED firmware update procedure (test on staging IED → deploy to production IED)",
              "Runbook-06: Edge ML model update (retrain → validate → deploy to IED via IEM)",
              "Runbook-07: Emergency rollback procedure (triggers, decision matrix, step-by-step revert)",
              "All runbooks: version-controlled in Git, reviewed quarterly"] },
        ]
      },
    ],
  },
  {
    id: "DOM-12", seq: 12, color: C.green, label: "Additional Integrations",
    icon: "🔌", phase: "Optional / Phase 2", effortDays: 40,
    desc: "Extended integrations NOT covered in the core interfaces: Supplier Collaboration, ECAD→TC, Polarion ALM, OPC-UA Unified Namespace (UNS), MQTT Broker, Asset Administration Shell (AAS), EPCIS 2.0, and Time-Series DB.",
    why: "Without Extended Integrations → digital thread is incomplete. AAS/UNS/EPCIS interoperability is required for Industry 4.0 maturity.",
    items: [
      {
        id: "D12-I01", name: "Supplier Collaboration Portal (TC Share)",
        tool: "TC Supplier Collaboration / Teamcenter", effort: 5, risk: 3,
        desc: "Extend TC to connect external suppliers for controlled BOM/drawing sharing, supplier feedback on tolerances, and supplier quality pre-submission.",
        tasks: [
          { id: "D12-I01-T1", type: "configure", name: "Configure TC Supplier Collaboration portal",
            dod: ["TC Share portal deployed: suppliers can login via external URL without VPN",
              "Controlled sharing: TC shares only Released ItemRevisions — not In-Work or Unreleased",
              "Supplier access: read-only for drawings and specifications; write-only for supplier feedback forms",
              "Supplier feedback form: supplier can submit deviation requests, tolerance queries — routed to engineering in TC",
              "Audit trail: every supplier download logged in TC — date, user, document, revision",
              "Watermarking: downloaded PDFs auto-watermarked with supplier name and download date"] },
          { id: "D12-I01-T2", type: "configure", name: "Connect Supplier Quality Pre-submission to Opcenter incoming inspection",
            dod: ["Supplier submits quality data (COC, CMM report) via TC Share portal before shipment",
              "TC Share triggers AIG: quality submission data → Opcenter incoming inspection queue item",
              "Receiving team sees expected quality data in Opcenter before physical delivery — confirmed in UAT",
              "Rejected supplier submission: notification sent to supplier via portal within 1 business day"] },
        ]
      },
      {
        id: "D12-I02", name: "ECAD → TC Integration (PCB → Digital Thread)",
        tool: "Xpedition / Allegro PCB / TC Active Workspace", effort: 6, risk: 3,
        desc: "Integrate ECAD (PCB design) tools with TC to close the digital thread for mechatronic products.",
        tasks: [
          { id: "D12-I02-T1", type: "configure", name: "Integrate ECAD tool with TC via ODB++/IDF/IDX adapter",
            dod: ["ECAD-TC adapter installed: design → TC Dataset via ODB++/IDX format",
              "TC EBOM: PCB assembly (PCBA) component linked to TC Item — ECAD BOM items match TC BOM items",
              "TC Dataset: native PCB design file, Gerber outputs, and BOM report stored as Datasets on TC ItemRevision",
              "ECAD change → TC ECR triggered: PCB revision change auto-creates TC ECR for engineering review",
              "TC EBOM to MCAD: PCB outline (STEP/IDF) referenced in NX MCAD assembly — co-design confirmed"] },
        ]
      },
      {
        id: "D12-I03", name: "Polarion ALM → TC Requirements Traceability",
        tool: "Siemens Polarion / TC ALM Integration", effort: 4, risk: 2,
        desc: "Link Polarion ALM system requirements to TC EBOM items and design decisions for regulated industries.",
        tasks: [
          { id: "D12-I03-T1", type: "configure", name: "Configure Polarion→TC bidirectional requirements traceability",
            dod: ["Polarion-TC integration adapter installed: bi-directional link between Polarion Work Items and TC items",
              "Requirements traceability matrix: Polarion Requirement → TC ItemRevision (design) → TC Mfg0BvrOperation (manufacturing)",
              "Impact analysis: change to Polarion requirement flags linked TC items for engineering review — confirmed",
              "Verification closure: V&V test result in Polarion closes requirement satisfaction — visible in TC traceability view",
              "Industry requirement coverage: all mandatory requirements traced to design items in TC"] },
        ]
      },
      {
        id: "D12-I04", name: "OPC-UA Unified Namespace (UNS) Aggregation Server",
        tool: "Unified Automation / UaGateway / Eclipse Milo", effort: 5, risk: 3,
        desc: "Deploy an OPC-UA Aggregation Server implementing the Unified Namespace (UNS) pattern.",
        tasks: [
          { id: "D12-I04-T1", type: "configure", name: "Deploy OPC-UA UNS Aggregation Server",
            dod: ["OPC-UA Aggregation Server deployed in OT DMZ",
              "All 20+ CNC machines connected as UA subscriptions to the aggregation server",
              "UNS namespace: Enterprise/Plant/Line/Station/Device/Measurement hierarchy — browseable from any OPC-UA client",
              "Any authorised client (SFC, Edge, Scada) can subscribe to any machine signal via single UNS endpoint",
              "UNS access control: roles define which clients can subscribe to which namespace nodes",
              "UNS bridge to MQTT: UNS server bridges OPC-UA data to MQTT topic hierarchy for IoT consumers"] },
        ]
      },
      {
        id: "D12-I05", name: "MQTT Broker for Plant IoT",
        tool: "HiveMQ Enterprise / Eclipse Mosquitto", effort: 3, risk: 2,
        desc: "Deploy a dedicated MQTT broker in the OT DMZ to decouple machine data producers from data consumers.",
        tasks: [
          { id: "D12-I05-T1", type: "configure", name: "Deploy MQTT broker with topic hierarchy and access control",
            dod: ["MQTT broker deployed in OT DMZ with TLS 1.2+",
              "MQTT topic hierarchy: {site}/{line}/{machine}/{signal} — published by Edge IEDs",
              "MQTT access control: client certificate + ACL rules — each IED can only publish its own topics",
              "Hub subscription: Hub MQTT client subscribes to {site}/# — receives all plant data",
              "SFC subscription: SFC subscribes to alarm topics — real-time alarm routing",
              "MQTT bridge to Hub: Edge → MQTT broker → Hub MQTT connector (cloud bridge configured)"] },
        ]
      },
      {
        id: "D12-I06", name: "Asset Administration Shell (AAS / RAMI 4.0)",
        tool: "Eclipse BaSyx / Siemens AAS Server", effort: 5, risk: 3,
        desc: "Implement Asset Administration Shells for all production assets per IEC 63278 / Plattform Industrie 4.0.",
        tasks: [
          { id: "D12-I06-T1", type: "configure", name: "Create AAS for all production assets and link to Hub Digital Twin",
            dod: ["AAS Server deployed: AAS REST API V3 accessible",
              "AAS created for each machine: Identification submodel, Nameplate submodel, Operations submodel",
              "AAS Nameplate submodel: ECLASS classification, manufacturer name, model, serial number — per IEC 61360",
              "AAS Digital Nameplate linked to Hub Asset: AAS ID = Hub Asset ID — bidirectional reference confirmed",
              "AAS Technical Data submodel: OPC-UA endpoint, supported signal list, sampling capabilities — discoverable",
              "AAS interoperability test: third-party tool can browse and read any AAS without configuration"] },
        ]
      },
      {
        id: "D12-I07", name: "EPCIS 2.0 Track & Trace (Serialisation Compliance)",
        tool: "EPCIS 2.0 Repository / GS1 / Antares Vision", effort: 4, risk: 2,
        desc: "Implement EPCIS 2.0 event capture for serialised product genealogy for regulatory traceability compliance.",
        tasks: [
          { id: "D12-I07-T1", type: "configure", name: "Configure EPCIS 2.0 event capture from Opcenter at key process stages",
            dod: ["EPCIS 2.0 repository deployed: GS1 EPCIS 2.0 compliant",
              "Commissioning event: Opcenter Container serial assigned → EPCIS ObjectEvent type=ADD bizStep=commissioning",
              "Aggregation event: sub-assembly packed into assembly → EPCIS AggregationEvent",
              "Shipping event: Opcenter Container shipped → EPCIS ObjectEvent bizStep=shipping",
              "Recall query: EPCIS query for serial number returns complete event history in ≤5 seconds",
              "EPCIS events linked to SAP serial record: cross-reference stored in SAP batch class"] },
        ]
      },
      {
        id: "D12-I08", name: "On-Premises Time-Series DB (InfluxDB / TimescaleDB)",
        tool: "InfluxDB 3.0 / TimescaleDB / Grafana", effort: 4, risk: 2,
        desc: "Deploy an on-premises time-series database as a local data historian for data sovereignty and low-latency SPC.",
        tasks: [
          { id: "D12-I08-T1", type: "configure", name: "Deploy on-premises TSDB for local data historian",
            dod: ["InfluxDB 3.0 (or TimescaleDB) deployed on dedicated server in IT DMZ",
              "Edge IEDs write to on-premises TSDB via Telegraf agent",
              "Opcenter SPC reads from on-premises TSDB for real-time SPC calculations (sub-100ms latency)",
              "Data retention: 2 years of raw 1-second machine data on-premises (hot storage)",
              "Hub synchronisation: InfluxDB replicates to Insights Hub daily for long-term analytics",
              "Grafana dashboard: local real-time machine data dashboard accessible from OT engineering workstations"] },
        ]
      },
    ],
  },
  {
    id: "DOM-13", seq: 13, color: C.purple, label: "Project Governance",
    icon: "📋", phase: "Throughout", effortDays: 12,
    desc: "Maintain project governance throughout the CLM implementation: steering committee, risk register, change control board, issue management, and benefits realisation.",
    why: "Without Governance → scope creep, uncontrolled changes, and failed ROI. Structured governance is mandatory for a project of this scale.",
    items: [
      {
        id: "D13-I01", name: "Steering Committee & Project Reporting",
        tool: "Project Management Tool / PowerPoint / Confluence", effort: 3, risk: 1,
        desc: "Establish and run the CLM steering committee with monthly reviews, project dashboard, and formal escalation process.",
        tasks: [
          { id: "D13-I01-T1", type: "document", name: "Set up project governance and steering committee cadence",
            dod: ["Steering committee charter signed: members, meeting cadence (monthly), quorum, decision authority",
              "Project dashboard operational: RAG status per workstream, milestone tracker, budget vs actual",
              "Monthly steering report: 2-page executive summary — delivered 5 days before meeting",
              "Escalation process: any RED workstream requires escalation within 2 business days",
              "Project Sponsor engagement: sponsor attends ≥90% of steering committee meetings"] },
        ]
      },
      {
        id: "D13-I02", name: "Risk Register & Issue Log",
        tool: "JIRA / Confluence / Excel", effort: 3, risk: 1,
        desc: "Maintain live risk register and issue log throughout the project with regular reviews and mitigation tracking.",
        tasks: [
          { id: "D13-I02-T1", type: "document", name: "Maintain risk register and issue log with weekly reviews",
            dod: ["Risk register initiated at project kick-off: ≥15 identified risks with probability, impact, and mitigation",
              "Top 5 risks reviewed weekly in project team meeting — mitigation actions tracked to closure",
              "Issue log: all issues logged within 24h of discovery — severity, owner, due date, resolution",
              "Risk threshold: any risk with score ≥16 escalated to Steering Committee",
              "Risk review at each phase gate: risks re-assessed before transitioning to next project phase"] },
        ]
      },
      {
        id: "D13-I03", name: "Change Control Board (CCB) for Scope and Configuration",
        tool: "CCB Process / JIRA / Change Log", effort: 3, risk: 2,
        desc: "Operate Change Control Board for all scope changes, interface specification changes, and production configuration changes.",
        tasks: [
          { id: "D13-I03-T1", type: "document", name: "Operate CCB for all CLM project changes",
            dod: ["CCB charter: members, meeting cadence (bi-weekly), decision authority",
              "Change request template: impact on scope, timeline, budget, interfaces — mandatory fields",
              "All interface specification changes: approved by CCB before implementation begins",
              "Configuration change management (production): any config change requires CCB approval + test",
              "Change log: all approved changes tracked with implementation date, tester, and sign-off"] },
        ]
      },
      {
        id: "D13-I04", name: "Benefits Realisation Plan",
        tool: "Benefits Register / Monthly KPI Report", effort: 3, risk: 1,
        desc: "Track and report realised benefits from CLM implementation against targets agreed in project charter.",
        tasks: [
          { id: "D13-I04-T1", type: "document", name: "Establish and track benefits realisation for CLM ROI",
            dod: ["Benefits register completed at project start: 8 quantified benefits with baseline, target, and measurement method",
              "Benefit 1: ECO cycle time reduced from baseline to target — tracked monthly",
              "Benefit 2: Manual data re-entries per day reduced by ≥80% — tracked via process audit",
              "Benefit 3: OEE improvement from baseline to target — tracked in Hub OEE dashboard",
              "Benefit 4: First Pass Yield improvement from baseline — tracked in Opcenter/Hub quality dashboard",
              "Benefit 5: Unplanned downtime reduction (predictive maintenance) — tracked in SAP PM + Hub",
              "Benefits review: quarterly benefits realisation report presented to sponsor and customer executive",
              "12-month post-go-live review: formal ROI calculation — SI and customer co-sign benefits realisation report"] },
        ]
      },
    ],
  },
];

// ─── UTILITY HELPERS ─────────────────────────────────────────────────────
function domainProgress(domain, dod) {
  let done = 0, total = 0;
  domain.items.forEach(item =>
    item.tasks.forEach(task =>
      task.dod.forEach((_, i) => { total++; if (dod[`${task.id}-${i}`]) done++; })
    )
  );
  return { done, total, pct: total > 0 ? Math.round(done / total * 100) : 0 };
}

function escapeHtml(str) {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function downloadFile(content, filename, type) {
  const blob = new Blob([content], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ═══════════════════════════════════════════════════════════════════════
// PDF REPORT GENERATOR
// ═══════════════════════════════════════════════════════════════════════
function generateE2EPDFReport(dod) {
  const now = new Date();
  const totalDod = E2E_DOMAINS.reduce((s, d) => s + d.items.reduce((ss, i) => ss + i.tasks.reduce((ts, t) => ts + t.dod.length, 0), 0), 0);
  const doneDod = Object.values(dod).filter(Boolean).length;
  const pct = totalDod > 0 ? Math.round(doneDod / totalDod * 100) : 0;
  const totalDays = E2E_DOMAINS.reduce((s, d) => s + d.effortDays, 0);

  const styles = `
    <style>
      @page { size: A4; margin: 12mm 10mm; }
      * { margin: 0; padding: 0; box-sizing: border-box; }
      body { font-family: 'Inter', 'Segoe UI', Arial, sans-serif; font-size: 8px; color: #1a1a2e; line-height: 1.45; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .page { page-break-after: always; padding: 5px 0; }
      .page:last-child { page-break-after: auto; }
      .header { background: linear-gradient(135deg, #ff4444, #ff4f8b, #ffd600); color: #fff; padding: 16px 20px; border-radius: 8px; margin-bottom: 16px; }
      .header-title { font-family: 'Orbitron', sans-serif; font-size: 18px; font-weight: 900; letter-spacing: 1.5px; margin-bottom: 2px; }
      .header-sub { font-size: 7px; letter-spacing: 2px; text-transform: uppercase; opacity: 0.8; }
      .header-meta { display: flex; gap: 20px; font-size: 7px; opacity: 0.9; margin-top: 6px; flex-wrap: wrap; }
      .section-title { font-family: 'Orbitron', monospace; font-size: 11px; font-weight: 700; margin: 14px 0 8px; padding-bottom: 4px; border-bottom: 2px solid #e0e0e0; }
      .kpi-grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: 6px; margin-bottom: 14px; }
      .kpi-card { background: #f8f9fa; border-radius: 6px; padding: 10px 8px; text-align: center; border-left: 3px solid; }
      .kpi-value { font-family: 'Orbitron', monospace; font-size: 18px; font-weight: 700; }
      .kpi-label { font-size: 6px; text-transform: uppercase; letter-spacing: 0.8px; color: #888; margin-top: 2px; }
      table { width: 100%; border-collapse: collapse; font-size: 7px; margin-bottom: 10px; }
      thead th { background: #1a1a2e; color: #ddeeff; padding: 5px 4px; text-align: left; font-size: 6px; letter-spacing: 0.6px; text-transform: uppercase; }
      tbody td { padding: 4px; border-bottom: 1px solid #eee; font-size: 6.5px; vertical-align: top; }
      tbody tr:nth-child(even) { background: #f8f9fa; }
      .badge { display: inline-block; padding: 1px 6px; border-radius: 8px; font-size: 6px; font-weight: 600; }
      .badge-success { background: #d4edda; color: #155724; }
      .badge-warning { background: #fff3cd; color: #856404; }
      .badge-danger { background: #f8d7da; color: #721c24; }
      .badge-info { background: #d1ecf1; color: #0c5460; }
      .badge-purple { background: #e8d5f5; color: #6f42c1; }
      .mini-progress { display: flex; align-items: center; gap: 4px; }
      .mini-progress-bar { width: 40px; height: 3px; background: #e0e0e0; border-radius: 2px; overflow: hidden; }
      .mini-progress-fill { height: 100%; border-radius: 2px; }
      .domain-card { background: #fff; border: 1px solid #e0e0e0; border-radius: 6px; padding: 10px 12px; margin-bottom: 8px; page-break-inside: avoid; }
      .domain-header { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
      .domain-name { font-family: 'Orbitron', monospace; font-size: 10px; font-weight: 700; }
      .domain-desc { font-size: 7px; color: #666; margin-bottom: 6px; line-height: 1.4; }
      .item-row { padding: 4px 6px; border-bottom: 1px solid #f5f5f5; display: flex; justify-content: space-between; align-items: center; font-size: 6.5px; }
      .dod-list { font-size: 6.5px; padding-left: 12px; }
      .dod-list li { padding: 1px 0; margin-bottom: 1px; }
      .dod-done { color: #999; text-decoration: line-through; }
      .info-box { background: #fff3cd; border-radius: 4px; padding: 8px 10px; margin: 8px 0; border-left: 3px solid #ffb300; font-size: 7px; }
      .footer { text-align: center; font-size: 6px; color: #aaa; margin-top: 20px; padding-top: 8px; border-top: 1px solid #eee; }
    </style>
  `;

  let html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>CLM E2E Implementation Report</title>${styles}</head><body>`;

  // PAGE 1: COVER & EXECUTIVE SUMMARY
  html += `<div class="page">`;
  html += `
    <div class="header">
      <div class="header-sub">Siemens Closed-Loop Manufacturing · Implementation Gap Coverage</div>
      <div class="header-title">E2E CLM IMPLEMENTATION REPORT</div>
      <div style="font-size: 9px; margin-top: 2px;">Comprehensive End-to-End Implementation Status</div>
      <div class="header-meta">
        <span>📅 ${now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</span>
        <span>🕐 ${now.toLocaleTimeString()}</span>
        <span>📋 13 Domains · ${E2E_DOMAINS.reduce((s,d)=>s+d.items.length,0)} Work Items</span>
        <span>✅ ${doneDod}/${totalDod} DoD Complete</span>
      </div>
    </div>
  `;

  html += `<div class="kpi-grid">`;
  [
    ["#ff4444", "13", "Domains"], ["#ffb300", E2E_DOMAINS.reduce((s,d)=>s+d.items.length,0), "Work Items"],
    ["#c084fc", totalDod, "DoD Criteria"], ["#00ff88", doneDod, "Completed"],
    ["#ffd600", pct+"%", "Progress"], ["#38a8ff", totalDays+"d", "Total Effort"]
  ].forEach(([c,v,l]) => {
    html += `<div class="kpi-card" style="border-left-color:${c};"><div class="kpi-value" style="color:${c};">${v}</div><div class="kpi-label">${l}</div></div>`;
  });
  html += `</div>`;

  html += `<div class="section-title">📊 EXECUTIVE SUMMARY</div>`;
  html += `<div style="font-size:8px;color:#555;line-height:1.5;margin-bottom:12px;">
    This report covers <strong>13 implementation domains</strong> required for a production-grade CLM deployment.
    Currently, <strong>${doneDod} of ${totalDod}</strong> Definition of Done criteria have been completed (<strong>${pct}%</strong>).
  </div>`;

  html += `<div class="section-title">🏗️ DOMAIN PROGRESS SUMMARY</div>`;
  html += `<table><thead><tr>
    <th>#</th><th>Domain</th><th>Phase</th><th>Items</th><th>Effort</th><th>DoD</th><th>Progress</th><th>Status</th>
  </tr></thead><tbody>`;

  E2E_DOMAINS.forEach((dom, idx) => {
    const prog = domainProgress(dom, dod);
    const pc = prog.pct >= 75 ? '#00ff88' : prog.pct >= 50 ? '#ffb300' : prog.pct >= 25 ? '#fb923c' : '#ff4444';
    const status = prog.pct === 100 ? 'Complete' : prog.pct > 0 ? 'In Progress' : 'Not Started';
    const sb = status === 'Complete' ? 'success' : status === 'In Progress' ? 'warning' : 'info';
    html += `<tr>
      <td style="text-align:center;">${idx+1}</td>
      <td><strong style="color:${dom.color};">${dom.label}</strong></td>
      <td>${dom.phase}</td><td style="text-align:center;">${dom.items.length}</td>
      <td style="text-align:center;">${dom.effortDays}d</td>
      <td style="text-align:center;">${prog.done}/${prog.total}</td>
      <td><div class="mini-progress"><div class="mini-progress-bar"><div class="mini-progress-fill" style="width:${prog.pct}%;background:${pc};"></div></div><span style="font-size:6px;color:${pc};">${prog.pct}%</span></div></td>
      <td><span class="badge badge-${sb}">${status}</span></td>
    </tr>`;
  });
  html += `</tbody></table>`;

  html += `<div class="section-title">⚠️ WHY THESE 13 DOMAINS ARE MANDATORY</div>`;
  const reasons = [
    ["Platform Setup", "Without it, nothing runs."],
    ["TC Configuration", "BMIDE types don't exist; CN4T has nothing to sync."],
    ["Opcenter Config", "No Site, Resources, or DataCollection library."],
    ["Security/PKI", "OPC-UA connections fail without proper certificates."],
    ["Master Data", "Specs, BOMs, Products don't exist on Day 1."],
    ["ISA-95 Compliance", "Regulators reject non-compliant systems."],
    ["Process Redesign", "Technology without process change = no ROI."],
    ["Testing (SIT/UAT)", "Go-live risk unacceptable without structured testing."],
    ["Training", "Operators can't use SFC without proper training."],
    ["Cutover Plan", "Data loss risk without structured cutover."],
    ["Monitoring", "First incident takes hours to diagnose."],
    ["Governance", "Scope creep leads to failed projects."],
    ["Extended Integrations", "Digital thread incomplete without AAS/UNS."],
  ];
  reasons.forEach(([title, reason]) => {
    html += `<div style="display:flex;gap:6px;padding:2px 0;font-size:7px;"><span style="color:#ff4444;">⚠</span><span><strong>${title}:</strong> ${reason}</span></div>`;
  });
  html += `</div>`;

  // DETAILED DOMAIN PAGES
  E2E_DOMAINS.forEach((dom, domIdx) => {
    const prog = domainProgress(dom, dod);
    html += `<div class="page">`;
    html += `<div class="domain-card" style="border-top:3px solid ${dom.color};">
      <div class="domain-header">
        <span style="font-size:20px;">${dom.icon}</span>
        <div>
          <div class="domain-name" style="color:${dom.color};">DOM-${String(domIdx+1).padStart(2,'0')}: ${dom.label}</div>
          <div style="font-size:7px;color:#888;">Phase: ${dom.phase} · ${dom.effortDays} person-days · ${dom.items.length} work items</div>
        </div>
        <div style="margin-left:auto;text-align:center;">
          <div style="font-family:'Orbitron',monospace;font-size:14px;font-weight:700;color:${prog.pct===100?'#00ff88':dom.color};">${prog.pct}%</div>
          <div style="font-size:6px;color:#888;">${prog.done}/${prog.total} DoD</div>
        </div>
      </div>
      <div class="domain-desc">${dom.desc}</div>
      ${dom.why ? `<div class="info-box"><strong>Why This Matters:</strong> ${dom.why}</div>` : ''}
    </div>`;

    dom.items.forEach((item, itemIdx) => {
      let itemDone = 0, itemTotal = 0;
      item.tasks.forEach(t => t.dod.forEach((_, i) => { itemTotal++; if (dod[`${t.id}-${i}`]) itemDone++; }));
      const itemPct = itemTotal > 0 ? Math.round(itemDone / itemTotal * 100) : 0;
      const ipc = itemPct === 100 ? '#00ff88' : itemPct >= 50 ? '#ffb300' : '#ff4444';

      html += `<div style="background:#f8f9fa;border-radius:4px;padding:8px 10px;margin-bottom:6px;page-break-inside:avoid;">
        <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:4px;">
          <div><strong style="font-size:8px;">${String(itemIdx+1).padStart(2,'0')}. ${item.name}</strong>
            <div style="font-size:6px;color:#888;">${item.desc}</div></div>
          <div style="text-align:center;"><span style="font-size:7px;font-weight:600;color:${ipc};">${itemPct}%</span></div>
        </div>
        <div style="display:flex;gap:4px;margin-bottom:4px;">
          <span class="badge badge-purple">${item.effort}d</span>
          <span class="badge badge-${item.risk>=3?'danger':'warning'}">${RISK_M[item.risk].l} Risk</span>
        </div>
        <div style="height:3px;background:#e0e0e0;border-radius:2px;margin-bottom:4px;">
          <div style="height:100%;width:${itemPct}%;background:${ipc};border-radius:2px;"></div>
        </div>
        <ol class="dod-list">
          ${item.tasks.map(task => task.dod.map((dodText, di) => {
            const isDone = !!dod[`${task.id}-${di}`];
            return `<li class="${isDone ? 'dod-done' : ''}">${isDone ? '✅' : '☐'} <strong>${task.name}:</strong> ${dodText}</li>`;
          }).join('')).join('')}
        </ol>
      </div>`;
    });
    html += `</div>`;
  });

  // FINAL PAGE
  html += `<div class="page">`;
  html += `<div class="section-title">📦 EXPORT METADATA</div>`;
  html += `<div style="font-size:7px;color:#555;line-height:1.5;">
    <strong>Report Type:</strong> E2E CLM Implementation Gap Coverage<br/>
    <strong>Generated:</strong> ${now.toLocaleString()}<br/>
    <strong>Version:</strong> 2.0 Production Ready<br/>
    <strong>Domains:</strong> 13 · <strong>Work Items:</strong> ${E2E_DOMAINS.reduce((s,d)=>s+d.items.length,0)}<br/>
    <strong>Total DoD:</strong> ${totalDod} · <strong>Completed:</strong> ${doneDod} (${pct}%)<br/>
    <strong>Total Effort:</strong> ${totalDays} person-days
  </div>`;
  html += `<div class="footer"><strong>CLM E2E IMPLEMENTATION TRACKER</strong> · Siemens · Confidential<br/>Generated: ${now.toLocaleString()}</div>`;
  html += `</div>`;

  html += `</body></html>`;

  const printWindow = window.open('', '_blank', 'width=1200,height=800');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 600);
  } else {
    downloadFile(html, `CLM_E2E_Report_${now.toISOString().slice(0,10)}.html`, "text/html");
    alert('Report opened in new window. If blocked, download as HTML and open in browser → Print → Save as PDF.');
  }
}

// ═══════════════════════════════════════════════════════════════════════
// UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════
function ProgressRing({ pct, color, size = 32 }) {
  const r = 11, circ = 2 * Math.PI * r, filled = (pct / 100) * circ;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32">
      <circle cx="16" cy="16" r={r} fill="none" stroke="rgba(255,255,255,.07)" strokeWidth="2.5"/>
      <circle cx="16" cy="16" r={r} fill="none" stroke={color} strokeWidth="2.5"
        strokeDasharray={`${filled} ${circ - filled}`} strokeDashoffset={circ * 0.25} strokeLinecap="round"/>
      <text x="16" y="20" textAnchor="middle" fontFamily="Orbitron,monospace" fontSize="8" fontWeight="700" fill={color}>{pct}</text>
    </svg>
  );
}

const Pill = ({ label, color, sm }) => (
  <span style={S.pill(color, sm)}>{label}</span>
);

function DodRow({ dodKey, text, dod, onToggle }) {
  const done = !!dod[dodKey];
  return (
    <div onClick={() => onToggle(dodKey)}
      style={{ display: "flex", alignItems: "flex-start", gap: 7, padding: "5px 4px",
        borderBottom: "1px solid rgba(255,255,255,.025)", cursor: "pointer" }}
      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,.025)"}
      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
      <div style={{ width: 13, height: 13, borderRadius: 2, flexShrink: 0, marginTop: 2,
        border: `1px solid ${done ? "#00ff88" : "rgba(0,229,255,.3)"}`,
        background: done ? "#00ff88" : "transparent",
        display: "flex", alignItems: "center", justifyContent: "center", transition: "all .14s" }}>
        {done && <span style={{ color: "#000", fontSize: 9, lineHeight: 1 }}>✓</span>}
      </div>
      <span style={{ fontSize: 10, lineHeight: 1.45, color: done ? "#2e4a60" : "#6a8fa8",
        textDecoration: done ? "line-through" : "none", flex: 1 }}>{text}</span>
    </div>
  );
}

function TaskCard({ task, dod, onToggle, color }) {
  const [open, setOpen] = useState(false);
  const done = task.dod.filter((_, i) => dod[`${task.id}-${i}`]).length;
  const tc = TYPE_M[task.type] || "#64b5d6";
  return (
    <div style={{ border: `1px solid rgba(255,255,255,.06)`, borderLeft: `2px solid ${tc}`,
      borderRadius: 3, overflow: "hidden", marginBottom: 8 }}>
      <div onClick={() => setOpen(o => !o)} style={{ padding: "8px 12px", display: "flex",
        alignItems: "center", gap: 10, cursor: "pointer", background: "#0b1d32" }}
        onMouseEnter={e => e.currentTarget.style.background = "#0d2035"}
        onMouseLeave={e => e.currentTarget.style.background = "#0b1d32"}>
        <div style={{ width: 20, height: 20, borderRadius: "50%", background: tc, flexShrink: 0,
          display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ color: "#000", fontSize: 9, fontWeight: 700 }}>{task.type[0].toUpperCase()}</span>
        </div>
        <span style={{ flex: 1, fontSize: 11, fontWeight: 500, color: "#ddeeff" }}>{task.name}</span>
        <span style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 8.5, color: tc }}>{done}/{task.dod.length}</span>
        <span style={{ color: tc, fontSize: 10, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>▸</span>
      </div>
      {open && (
        <div style={{ padding: "4px 8px 8px", background: "#071628" }}>
          {task.dod.map((text, i) => (
            <DodRow key={i} dodKey={`${task.id}-${i}`} text={text} dod={dod} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
}

function ItemCard({ item, dod, onToggle, domainColor }) {
  const [open, setOpen] = useState(false);
  let done = 0, total = 0;
  item.tasks.forEach(t => t.dod.forEach((_, i) => { total++; if (dod[`${t.id}-${i}`]) done++; }));
  const pct = total > 0 ? Math.round(done / total * 100) : 0;
  return (
    <div style={{ border: `1px solid ${domainColor}22`, borderRadius: 4, marginBottom: 12, overflow: "hidden" }}>
      <div onClick={() => setOpen(o => !o)}
        style={{ padding: "11px 14px", background: `${domainColor}06`, display: "flex", alignItems: "center",
          gap: 12, cursor: "pointer", borderBottom: open ? `1px solid ${domainColor}20` : "none" }}
        onMouseEnter={e => e.currentTarget.style.background = `${domainColor}0a`}
        onMouseLeave={e => e.currentTarget.style.background = `${domainColor}06`}>
        <ProgressRing pct={pct} color={pct === 100 ? "#00ff88" : domainColor} size={36}/>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#ddeeff", marginBottom: 3 }}>{item.name}</div>
          <div style={{ fontSize: 10, color: "#6a8fa8", lineHeight: 1.5 }}>{item.desc}</div>
          <div style={{ display: "flex", gap: 6, marginTop: 5, flexWrap: "wrap" }}>
            <Pill label={`${item.effort}d effort`} color={domainColor} sm/>
            <Pill label={RISK_M[item.risk].l + " risk"} color={RISK_M[item.risk].c} sm/>
            {item.tool && <Pill label={item.tool.split(" / ")[0]} color="#64b5d6" sm/>}
          </div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontFamily: "Orbitron,monospace", fontSize: 11, fontWeight: 700, color: pct === 100 ? "#00ff88" : domainColor }}>{pct}%</div>
          <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 8, color: "#6a8fa8", marginTop: 2 }}>{done}/{total} DoD</div>
          <div style={{ color: domainColor, fontSize: 11, marginTop: 4, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}>▸</div>
        </div>
      </div>
      {open && (
        <div style={{ padding: "12px 14px", background: "#071628" }}>
          {item.tasks.map(task => (
            <TaskCard key={task.id} task={task} dod={dod} onToggle={onToggle} color={domainColor}/>
          ))}
        </div>
      )}
    </div>
  );
}

function DomainPanel({ domain, dod, onToggle }) {
  const prog = domainProgress(domain, dod);
  return (
    <div style={{ padding: "14px 18px" }}>
      <div style={{ padding: "12px 16px", marginBottom: 14, background: `${domain.color}08`,
        border: `1px solid ${domain.color}30`, borderRadius: 4, display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
        <div style={{ fontSize: 28 }}>{domain.icon}</div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: "Orbitron,monospace", fontSize: 15, fontWeight: 700, color: domain.color, marginBottom: 4 }}>
            {domain.label}
          </div>
          <div style={{ fontSize: 10.5, color: "#6a8fa8", lineHeight: 1.6 }}>{domain.desc}</div>
          {domain.why && (
            <div style={{ fontSize: 9, color: "#ffb300", marginTop: 4, fontStyle: "italic" }}>
              ⚠ {domain.why}
            </div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 7, flexWrap: "wrap" }}>
            <Pill label={`Phase: ${domain.phase}`} color={domain.color}/>
            <Pill label={`${domain.effortDays} person-days`} color="#c084fc"/>
            <Pill label={`${domain.items.length} work items`} color="#38a8ff"/>
          </div>
        </div>
        <div style={{ textAlign: "center" }}>
          <ProgressRing pct={prog.pct} color={prog.pct === 100 ? "#00ff88" : domain.color} size={56}/>
          <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 8, color: "#6a8fa8", marginTop: 4 }}>
            {prog.done}/{prog.total} DoD
          </div>
        </div>
      </div>
      {domain.items.map(item => (
        <ItemCard key={item.id} item={item} dod={dod} onToggle={onToggle} domainColor={domain.color}/>
      ))}
    </div>
  );
}

function E2EDashboard({ dod }) {
  const totalDod = useMemo(() => E2E_DOMAINS.reduce((s, d) => s + d.items.reduce((ss, i) => ss + i.tasks.reduce((ts, t) => ts + t.dod.length, 0), 0), 0), []);
  const doneDod = Object.values(dod).filter(Boolean).length;
  const pct = totalDod > 0 ? Math.round(doneDod / totalDod * 100) : 0;

  return (
    <div style={{ padding: "16px 20px" }}>
      <div style={{ padding: "14px 18px", marginBottom: 16, background: "rgba(255,68,68,.06)",
        border: "1px solid rgba(255,68,68,.2)", borderRadius: 4 }}>
        <div style={{ fontFamily: "Orbitron,monospace", fontSize: 13, fontWeight: 700, color: C.red, marginBottom: 5 }}>
          ⚠ CLM IMPLEMENTATION GAPS — NOT COVERED IN APP.JSX
        </div>
        <div style={{ fontSize: 11, color: "#6a8fa8", lineHeight: 1.65 }}>
          This module covers <strong style={{ color: "#ddeeff" }}>13 implementation domains</strong> required for a
          production-grade end-to-end CLM deployment. These are the strategic, infrastructure, governance, and
          extended integration layers that make the difference between a proof-of-concept and a running production system.
        </div>
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          [C.red, "13", "Domains"], [C.amber, E2E_DOMAINS.reduce((s,d)=>s+d.items.length,0), "Work Items"],
          [C.purple, totalDod, "DoD Criteria"], [C.green, doneDod, "Completed"],
          [C.gold, pct+"%", "Progress"], [C.blue, E2E_DOMAINS.reduce((s,d)=>s+d.effortDays,0)+"d", "Total Effort"],
        ].map(([col, v, l]) => (
          <div key={l} style={S.kpiCard}>
            <div style={{ fontFamily: "Orbitron,monospace", fontSize: 18, fontWeight: 700, color: col }}>{v}</div>
            <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 7, color: C.dim, textTransform: "uppercase", letterSpacing: 1, marginTop: 2 }}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 8, letterSpacing: "2px", textTransform: "uppercase", color: C.dim, marginBottom: 10 }}>
        Implementation Domains
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(240px,1fr))", gap: 10 }}>
        {E2E_DOMAINS.map(domain => {
          const prog = domainProgress(domain, dod);
          return (
            <div key={domain.id} style={{ padding: "11px 14px", background: `${domain.color}07`,
              border: `1px solid ${domain.color}28`, borderRadius: 4 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 7 }}>
                <span style={{ fontSize: 18 }}>{domain.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: "Orbitron,monospace", fontSize: 10, fontWeight: 700,
                    color: domain.color }}>{domain.label}</div>
                  <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 7, color: C.dim, marginTop: 1 }}>
                    {domain.phase} · {domain.effortDays}d
                  </div>
                </div>
                <ProgressRing pct={prog.pct} color={prog.pct === 100 ? "#00ff88" : domain.color} size={34}/>
              </div>
              <div style={S.progressBar}>
                <div style={{ height: "100%", borderRadius: 2, background: prog.pct === 100 ? "#00ff88" : domain.color,
                  width: `${prog.pct}%`, transition: "width .4s" }}/>
              </div>
              <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 7, color: C.dim, marginTop: 4 }}>
                {prog.done}/{prog.total} · {domain.items.length} items
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ExportTab({ dod, dispatch }) {
  const fileRef = useRef();
  const [importStatus, setImportStatus] = useState(null);
  const totalDod = useMemo(() => E2E_DOMAINS.reduce((s, d) => s + d.items.reduce((ss, i) => ss + i.tasks.reduce((ts, t) => ts + t.dod.length, 0), 0), 0), []);
  const doneDod = Object.values(dod).filter(Boolean).length;
  const pct = totalDod > 0 ? Math.round(doneDod / totalDod * 100) : 0;

  const handleExportJSON = () => {
    const data = {
      metadata: { version: "2.0", exportDate: new Date().toISOString(), application: "CLM E2E Implementation Tracker",
        totalDomains: 13, totalItems: E2E_DOMAINS.reduce((s,d)=>s+d.items.length,0), totalDoD: totalDod, completedDoD: doneDod, overallProgress: pct },
      dod,
      domains: E2E_DOMAINS.map(dom => ({ id: dom.id, label: dom.label, progress: domainProgress(dom, dod) })),
    };
    downloadFile(JSON.stringify(data, null, 2), `clm_e2e_state_${new Date().toISOString().slice(0,10)}.json`, "application/json");
  };

  const handleImport = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({ type: 'loading', message: 'Reading file...' });
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = JSON.parse(ev.target.result);
        dispatch({ type: "IMPORT", payload: data.dod || data });
        setImportStatus({ type: 'success', message: `✅ State imported! ${Object.values(data.dod||data).filter(Boolean).length} DoD items restored.` });
        setTimeout(() => setImportStatus(null), 4000);
      } catch { setImportStatus({ type: 'error', message: '❌ Invalid JSON file' }); }
      if (fileRef.current) fileRef.current.value = '';
    };
    reader.readAsText(file);
  };

  return (
    <div style={{ padding: "24px 28px", maxWidth: 900 }}>
      <div style={{ fontFamily: "Orbitron,monospace", fontSize: 16, fontWeight: 700, color: C.red, marginBottom: 4 }}>📤 Export & Import E2E State</div>
      <div style={{ fontSize: 11, color: C.dim, marginBottom: 20, lineHeight: 1.6 }}>Export your complete E2E implementation progress or import a previously saved state.</div>

      {importStatus && (
        <div style={{ padding: "10px 14px", marginBottom: 16, borderRadius: 6,
          background: importStatus.type === 'success' ? 'rgba(0,255,136,.08)' : importStatus.type === 'error' ? 'rgba(255,68,68,.08)' : 'rgba(0,229,255,.08)',
          border: `1px solid ${importStatus.type === 'success' ? 'rgba(0,255,136,.3)' : importStatus.type === 'error' ? 'rgba(255,68,68,.3)' : 'rgba(0,229,255,.3)'}` }}>
          <span style={{ fontSize: 10, color: importStatus.type === 'error' ? C.red : C.text }}>{importStatus.message}</span>
        </div>
      )}

      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        {[[C.green, doneDod, "Completed"],[C.purple, totalDod-doneDod,"Remaining"],[C.gold,pct+"%","Progress"]].map(([c,v,l]) => (
          <div key={l} style={{...S.kpiCard, minWidth:70}}>
            <div style={{fontFamily:"Orbitron,monospace",fontSize:16,fontWeight:700,color:c}}>{v}</div>
            <div style={{fontSize:7,color:C.dim,textTransform:"uppercase"}}>{l}</div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
        <div onClick={handleExportJSON} style={{ padding: "14px", background: "rgba(0,255,136,.06)",
          border: "1px solid rgba(0,255,136,.25)", borderRadius: 6, cursor: "pointer", textAlign: "center" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(0,255,136,.12)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(0,255,136,.06)"}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>💾</div>
          <div style={{ fontFamily: "Orbitron,monospace", fontSize: 11, fontWeight: 700, color: C.green }}>Export JSON</div>
          <div style={{ fontSize: 8, color: C.dim, marginTop: 3 }}>Complete state with all DoD items</div>
        </div>
        <div onClick={() => generateE2EPDFReport(dod)} style={{ padding: "14px", background: "rgba(255,68,68,.06)",
          border: "2px solid rgba(255,68,68,.3)", borderRadius: 6, cursor: "pointer", textAlign: "center" }}
          onMouseEnter={e => e.currentTarget.style.background = "rgba(255,68,68,.12)"}
          onMouseLeave={e => e.currentTarget.style.background = "rgba(255,68,68,.06)"}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>📕</div>
          <div style={{ fontFamily: "Orbitron,monospace", fontSize: 11, fontWeight: 700, color: C.red }}>Export PDF Report</div>
          <div style={{ fontSize: 8, color: C.dim, marginTop: 3 }}>Comprehensive multi-page report</div>
          <div style={{ fontSize: 7, color: C.amber, marginTop: 2 }}>⭐ RECOMMENDED</div>
        </div>
      </div>

      <div onClick={() => fileRef.current.click()} style={{ padding: "14px", background: "rgba(56,168,255,.06)",
        border: "1px solid rgba(56,168,255,.25)", borderRadius: 6, cursor: "pointer", textAlign: "center", marginBottom: 14 }}
        onMouseEnter={e => e.currentTarget.style.background = "rgba(56,168,255,.12)"}
        onMouseLeave={e => e.currentTarget.style.background = "rgba(56,168,255,.06)"}>
        <div style={{ fontSize: 24, marginBottom: 6 }}>📥</div>
        <div style={{ fontFamily: "Orbitron,monospace", fontSize: 11, fontWeight: 700, color: C.blue }}>Import JSON State</div>
        <div style={{ fontSize: 8, color: C.dim, marginTop: 3 }}>Restore DoD progress from exported file</div>
      </div>
      <input ref={fileRef} type="file" accept=".json" style={{ display: "none" }} onChange={handleImport}/>

      <div style={{ padding: "14px 16px", background: "rgba(255,68,68,.06)",
        border: "1px solid rgba(255,68,68,.2)", borderRadius: 4, textAlign: "center" }}>
        <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 8, color: C.red, textTransform: "uppercase", letterSpacing: 1, marginBottom: 6 }}>Reset All Progress</div>
        <button onClick={() => { if (window.confirm("Reset all E2E DoD progress? Export first to save!")) dispatch({ type: "RESET" }); }}
          style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 9, padding: "6px 18px",
            background: "rgba(255,68,68,.15)", border: "1px solid rgba(255,68,68,.4)",
            color: C.red, cursor: "pointer", borderRadius: 4 }}>Reset All</button>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// REDUCER & PERSISTENCE
// ═══════════════════════════════════════════════════════════════════════
const STORAGE_KEY = "clm_e2e_v2";

function loadE2EState() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}"); } catch { return {}; }
}

function e2eReducer(state, action) {
  let next;
  switch (action.type) {
    case "TOGGLE_DOD": next = { ...state, [action.key]: !state[action.key] }; break;
    case "RESET": next = {}; break;
    case "IMPORT": next = { ...action.payload }; break;
    default: next = state;
  }
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
  return next;
}

// ═══════════════════════════════════════════════════════════════════════
// MAIN EXPORTED COMPONENT
// ═══════════════════════════════════════════════════════════════════════
export default function E2EClm() {
  const [dod, dispatch] = useReducer(e2eReducer, null, loadE2EState);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [activeDomain, setActiveDomain] = useState(E2E_DOMAINS[0].id);

  const toggleDod = useCallback(key => dispatch({ type: "TOGGLE_DOD", key }), []);

  const totalDod = useMemo(() => E2E_DOMAINS.reduce((s, d) => s + d.items.reduce((ss, i) => ss + i.tasks.reduce((ts, t) => ts + t.dod.length, 0), 0), 0), []);
  const doneDod = Object.values(dod).filter(Boolean).length;
  const pct = totalDod > 0 ? Math.round(doneDod / totalDod * 100) : 0;
  const activeDom = E2E_DOMAINS.find(d => d.id === activeDomain) || E2E_DOMAINS[0];

  const TABS = [
    { id: "dashboard", label: "📊 Dashboard" },
    { id: "domains", label: "📂 Domains" },
    { id: "export", label: "📤 Export/Import" },
  ];

  return (
    <div style={{ background: C.bg, color: C.text, fontFamily: "'Inter','DM Sans',sans-serif", minHeight: "100vh" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0,
        backgroundImage: "linear-gradient(rgba(0,229,255,.012) 1px,transparent 1px),linear-gradient(90deg,rgba(0,229,255,.012) 1px,transparent 1px)",
        backgroundSize: "44px 44px" }}/>
      <div style={{ position: "relative", zIndex: 5 }}>

        <div style={{ padding: "10px 20px 9px", background: "linear-gradient(180deg,rgba(255,68,68,.07),transparent)",
          borderBottom: "1px solid rgba(255,68,68,.2)", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 7, color: C.red, letterSpacing: "2px", textTransform: "uppercase", marginBottom: 2 }}>
              CLM IMPLEMENTATION GAP COVERAGE — NOT IN APP.JSX
            </div>
            <h1 style={{ fontFamily: "Orbitron,monospace", fontSize: 16, fontWeight: 900, letterSpacing: .5, margin: 0,
              background: "linear-gradient(90deg,#ff4444 0%,#ff4f8b 40%,#ffd600 80%)",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
              E2E CLM IMPLEMENTATION TRACKER
            </h1>
          </div>
          <div style={{ display: "flex", gap: 14, marginLeft: "auto", flexWrap: "wrap" }}>
            {[[C.red, "13", "Domains"], [C.amber, E2E_DOMAINS.reduce((s,d)=>s+d.items.length,0), "Items"],
              [C.purple, totalDod, "DoD"], [C.green, doneDod, "Done"],
              [C.gold, pct+"%", "Progress"], [C.blue, E2E_DOMAINS.reduce((s,d)=>s+d.effortDays,0)+"d", "Effort"]
            ].map(([col, v, l]) => (
              <div key={l} style={{ textAlign: "center" }}>
                <div style={{ fontFamily: "Orbitron,monospace", fontSize: 15, fontWeight: 700, color: col, lineHeight: 1 }}>{v}</div>
                <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 7, color: C.dim, textTransform: "uppercase", letterSpacing: 1, marginTop: 1 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", borderBottom: "1px solid rgba(255,255,255,.05)", background: C.bg2,
          position: "sticky", top: 0, zIndex: 40, overflowX: "auto" }}>
          {TABS.map(tab => {
            const on = activeTab === tab.id;
            return <div key={tab.id} onClick={() => setActiveTab(tab.id)} style={S.tab(on, C.red)}>{tab.label}</div>;
          })}
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, paddingRight: 14, flexShrink: 0 }}>
            <div style={{ width: 90, height: 3, background: "#0b1d32", borderRadius: 2 }}>
              <div style={{ height: "100%", borderRadius: 2, background: `linear-gradient(90deg,${C.red},${C.gold})`,
                width: `${pct}%`, transition: "width .4s" }}/>
            </div>
            <span style={{ fontFamily: "Orbitron,monospace", fontSize: 9, fontWeight: 700, color: C.red }}>{pct}%</span>
          </div>
        </div>

        <div style={{ maxHeight: "calc(100vh - 90px)", overflowY: "auto" }}>
          {activeTab === "dashboard" && <E2EDashboard dod={dod}/>}

          {activeTab === "domains" && (
            <div style={{ display: "flex", minHeight: "calc(100vh - 92px)" }}>
              <div style={{ width: 210, flexShrink: 0, borderRight: "1px solid rgba(255,255,255,.05)",
                background: C.bg2, padding: "10px 6px", overflowY: "auto" }}>
                {E2E_DOMAINS.map(dom => {
                  const prog = domainProgress(dom, dod);
                  const on = dom.id === activeDomain;
                  return (
                    <div key={dom.id} onClick={() => setActiveDomain(dom.id)}
                      style={{ padding: "8px 10px", borderRadius: 3, cursor: "pointer", marginBottom: 4,
                        border: `1px solid ${on ? dom.color + "40" : "transparent"}`,
                        background: on ? `${dom.color}08` : "transparent", transition: "all .14s" }}
                      onMouseEnter={e => !on && (e.currentTarget.style.background = C.bg3)}
                      onMouseLeave={e => !on && (e.currentTarget.style.background = "transparent")}>
                      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 4 }}>
                        <span style={{ fontSize: 14 }}>{dom.icon}</span>
                        <div style={{ fontFamily: "Orbitron,monospace", fontSize: 8, fontWeight: 600,
                          color: on ? dom.color : C.dim, lineHeight: 1.3 }}>{dom.label}</div>
                      </div>
                      <div style={{ height: 2, background: "rgba(255,255,255,.06)", borderRadius: 1 }}>
                        <div style={{ height: "100%", borderRadius: 1,
                          background: prog.pct === 100 ? "#00ff88" : dom.color, width: `${prog.pct}%` }}/>
                      </div>
                      <div style={{ fontFamily: "Share Tech Mono,monospace", fontSize: 7, color: C.dim, marginTop: 3 }}>
                        {prog.done}/{prog.total} DoD
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ flex: 1, overflowY: "auto" }}>
                <DomainPanel domain={activeDom} dod={dod} onToggle={toggleDod}/>
              </div>
            </div>
          )}

          {activeTab === "export" && <ExportTab dod={dod} dispatch={dispatch}/>}
        </div>
      </div>
    </div>
  );
}